// src/components/leave/LeaveRequestCreateModal.jsx
//
// Leave Request Create Modal
// ==========================
//
// PURPOSE
// -------
// This modal creates:
//
// 1) Normal leave requests
// 2) Special cancellation requests (LeaveType code = "CANCELLATION")
//
// CANCELLATION WORKFLOW
// ---------------------
// The backend implements special rules:
//
// - user chooses which existing leave request is being cancelled
// - if the target request is still PENDING:
//     backend auto-cancels it immediately
// - if the target request is APPROVED:
//     backend creates a cancellation workflow request that must be approved
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// 1) HTTP is authoritative.
//    - this component only sends the creation payload
//    - it does not simulate workflow decisions locally
//
// 2) apiClient owns refresh/session recovery.
//    - this component does not refresh tokens itself
//
// 3) Multi-tab-safe auth behavior.
//    - on auth failures we call softLogout()
//    - we never call hard logout here
//
// POLAND-FRIENDLY UX
// ------------------
// - days mode is the default
// - hours mode is opt-in and only available for leave types that support partial day
//
// IMPORTANT IMPLEMENTATION NOTES
// ------------------------------
// - Dictionary loading and cancellation-target loading are separate concerns
// - We do NOT reload the whole dictionary every time leaveTypeId changes
// - We keep the modal resilient to unmount/close while requests are in flight
//
// NOTE ON TIME HANDLING
// ---------------------
// For day-based mode we intentionally build local-midnight Date objects and send ISO strings.
// This matches your current backend expectation for Warsaw-style full-day boundaries.

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LeaveRequestCreateModal.css";
import LeaveTimeIntervalPicker from "./LeaveTimeIntervalPicker";
import LeaveTypePicker from "./LeaveTypePicker";

import AuthContext from "../../context/AuthContext";
import { fetchVacationSettings } from "../../services/vacationSettingsService";
import { fetchUsers } from "../../services/userService";
import {
  createLeaveRequest,
  fetchLeaveApprovers,
  fetchTimeSettlementCandidates,
  listCancellableLeaves,
} from "../../services/leaveRequestsService";
import { getTranslatedApiErrorMessage, isRequestCanceled } from "../../services/httpUtils";
import { formatLeaveBusinessDateRange } from "../../utils/leaveDateUtils";

const DEV = process.env.NODE_ENV !== "production";
const AUTO_APPROVER_VALUE = "AUTO";
const TIME_SETTLEMENT_MODE_AUTO = "AUTO_FIFO";
const TIME_SETTLEMENT_MODE_MANUAL = "MANUAL";
const TIME_SETTLEMENT_MODE_NONE = "NONE";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function asBool(value) {
  if (value === true || value === false) return value;
  if (value == null) return false;

  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
}

function safeStr(value) {
  return value == null ? "" : String(value);
}

function normalizeCode(code) {
  const c = String(code || "").trim();
  return c ? c.toUpperCase() : "";
}

function getEntitlementContextKind(code) {
  const c = normalizeCode(code);

  if (["FATHERS_LEAVE", "FATHER_LEAVE", "PATERNITY_LEAVE", "PATERNITY"].includes(c)) {
    return "FATHER";
  }

  if (["CHILDCARE_ART_188", "CHILDCARE_188"].includes(c)) {
    return "CHILDCARE_188";
  }

  if (["CARE_LEAVE", "CARE_LEAVE_SHORT"].includes(c)) {
    return "CARE_LEAVE";
  }

  if (["EDUCATIONAL_LEAVE", "TRAINING_LEAVE", "STUDY_LEAVE"].includes(c)) {
    return "EDUCATION";
  }

  if ([
    "HOLIDAY_SATURDAY_DAY_OFF",
    "HOLIDAY_COMPENSATION_DAY_OFF",
    "HOLIDAY_ON_SATURDAY_DAY_OFF",
    "DAY_OFF_FOR_HOLIDAY_ON_SATURDAY",
  ].includes(c)) {
    return "HOLIDAY_COMPENSATION";
  }

  if (["BLOOD_DONATION", "BLOOD_DONATION_LEAVE", "BLOOD_DONOR_LEAVE"].includes(c)) {
    return "BLOOD_DONATION";
  }

  if (["MATERNITY_LEAVE", "MATERNITY", "MATERNITY_BENEFIT"].includes(c)) {
    return "MATERNITY";
  }

  if (["PARENTAL_LEAVE", "PARENTAL", "PARENTAL_BENEFIT"].includes(c)) {
    return "PARENTAL";
  }

  if (["SICK_LEAVE", "L4"].includes(c)) {
    return "SICK_LEAVE";
  }

  if (["PREGNANCY_MEDICAL_EXAM", "PREGNANCY_EXAM"].includes(c)) {
    return "PREGNANCY_MEDICAL_EXAM";
  }

  if (["ADOPTION_MATERNITY_LEAVE", "MATERNITY_EQUIVALENT_ADOPTION_LEAVE"].includes(c)) {
    return "ADOPTION_MATERNITY";
  }

  if (["SUPPLEMENTARY_MATERNITY_LEAVE", "SUPPLEMENTARY_MATERNITY"].includes(c)) {
    return "SUPPLEMENTARY_MATERNITY";
  }

  if (["CARE_ALLOWANCE_ABSENCE", "CHILD_SICKNESS_CARE"].includes(c)) {
    return "CARE_ALLOWANCE";
  }

  if (["CHILDCARE_LEAVE_LONG", "UNPAID_CHILDCARE_LEAVE"].includes(c)) {
    return "CHILDCARE_LONG";
  }

  if (["JOB_SEARCH_LEAVE"].includes(c)) {
    return "JOB_SEARCH";
  }

  return null;
}

function toLocalISODate(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysLocal(dateStrYYYYMMDD, days) {
  const d = new Date(`${dateStrYYYYMMDD}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalISODate(d);
}

function fmtDateTimeLocal(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function sortLeaveTypes(list) {
  const arr = Array.isArray(list) ? [...list] : [];

  arr.sort((a, b) => {
    const aName = safeStr(a.name_pl || a.name_en || a.code);
    const bName = safeStr(b.name_pl || b.name_en || b.code);
    return aName.localeCompare(bName, "pl");
  });

  return arr;
}


function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function isPrivilegedRole(role) {
  return ["superuser", "admin"].includes(normalizeRole(role));
}

function formatEmployeeOption(user) {
  if (!user) return "—";
  const first = user.first_name || "";
  const last = user.last_name || "";
  const full = `${first} ${last}`.trim();
  const username = user.username ? ` (${user.username})` : "";
  return full ? `${full}${username}` : user.username || `User #${user.id}`;
}

function getDefaultSickPayrollRatePercent(reasonType) {
  const reason = normalizeCode(reasonType || "STANDARD");
  if (["PREGNANCY", "ACCIDENT_AT_WORK", "OCCUPATIONAL_DISEASE"].includes(reason)) {
    return "100.00";
  }
  return "80.00";
}

function toNullableInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

function isAuthFailure(err) {
  const status = err?.response?.status;
  const code = err?.code;

  return (
    status === 401 ||
    code === "SESSION_ID_REQUIRED" ||
    code === "REFRESH_FAILED"
  );
}

function formatApproverLabel(user) {
  if (!user) return "—";

  const first = user.first_name || "";
  const last = user.last_name || "";
  const full = `${first} ${last}`.trim();

  if (full) return `${full} (${user.username})`;
  return user.username || `User #${user.id}`;
}

function formatCancellableLeaveOption(row, t) {
  const typeLabel =
    row?.leaveType?.name_pl ||
    row?.leaveType?.name_en ||
    row?.leaveType?.code ||
    t("leaves:modal.defaultTypeLabel", { defaultValue: "Leave" });

  const status = row?.status || "—";

  const range = formatLeaveBusinessDateRange(row);

  return `#${row.id} • ${typeLabel} • ${range} • ${status}`;
}

function getSettlementCandidateId(row) {
  const raw = row?.id ?? row?.leave_request_id ?? row?.leaveRequestId ?? row?.request_id;
  const value = raw == null ? "" : String(raw).trim();
  return value || null;
}

function safePositiveInt(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.trunc(n));
}

function formatMinutesCompact(totalMinutes) {
  const minutes = safePositiveInt(totalMinutes, 0);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function getCandidateRemainingMinutes(row) {
  return safePositiveInt(row?.remaining_minutes ?? row?.remainingMinutes, 0);
}

function formatTimeSettlementCandidateLabel(row, t) {
  const id = getSettlementCandidateId(row);
  const typeLabel =
    row?.leave_type_name_pl ||
    row?.leave_type_name_en ||
    row?.leave_type_code ||
    t("leaves:modal.defaultTypeLabel", { defaultValue: "Leave" });
  const range = formatLeaveBusinessDateRange(row);
  const remaining = formatMinutesCompact(getCandidateRemainingMinutes(row));

  return `#${id} • ${typeLabel} • ${range} • ${remaining} open`;
}

function calculateLocalDateTimeDurationMinutes(startValue, endValue) {
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;
  if (end <= start) return null;
  return Math.round((end.getTime() - start.getTime()) / 60000);
}

function getInitialFormState() {
  return {
    leaveTypeId: "",
    draft: false,
    note: "",
    approverId: AUTO_APPROVER_VALUE,

    useHoursMode: false,

    startDate: "",
    endDateInclusive: "",

    startDT: "",
    endDT: "",

    cancellationTargetId: "",

    fatherEventType: "BIRTH",
    fatherChildName: "",
    fatherChildBirthDate: "",
    fatherAdoptionFinalDate: "",

    childcareChildName: "",
    childcareChildBirthDate: "",
    childcareOtherParentDeclarationConfirmed: false,

    careRecipientName: "",
    careRecipientRelation: "",
    careSameHousehold: false,
    careSeriousMedicalReasonDeclared: false,

    educationReason: "",
    educationInstitutionName: "",
    educationStudyProgramName: "",
    educationExamDate: "",
    educationDiplomaExamDate: "",
    educationLastYearOfStudies: false,
    educationEmployerConsentConfirmed: false,
    educationCustomEntitlementDays: "",

    holidayDate: "",
    holidayName: "",
    holidaySettlementPeriodStart: "",
    holidaySettlementPeriodEnd: "",
    holidayCompensationMinutes: "480",
    holidayMustBeTakenUntil: "",

    bloodDonationDate: "",
    bloodDonationCenterName: "",
    bloodCertificateNumber: "",
    bloodCertificateReceived: false,
    bloodDonationSuccessful: false,
    bloodIncludesNextDayOff: true,
    bloodMedicalExamOnly: false,

    maternityExpectedBirthDate: "",
    maternityChildBirthDate: "",
    maternityNumberOfChildrenAtBirth: "1",
    maternityPreBirthLeaveStartDate: "",
    maternityLeaveVariant: "STANDARD",

    parentalEventType: "BIRTH",
    parentalChildName: "",
    parentalChildBirthDate: "",
    parentalAdoptionFinalDate: "",
    parentalNumberOfChildrenAtBirth: "1",
    parentalHasZaZyciemCertificate: false,
    parentalLeaveVariant: "STANDARD_70",
    parentalCustomPaymentRatePercent: "",
    parentalOtherParentDeclaredUsageWeeks: "0",
    parentalOwnNonTransferablePartUsed: false,
    parentalCombinedWithWork: false,
    parentalWorkFractionDuringLeave: "",
    parentalExtendedEntitlementWeeks: "",

    sickReasonType: "STANDARD",
    sickCertificateSource: "MANUAL",
    sickCertificateNumber: "",
    sickIssueDate: "",
    sickReceivedAt: "",
    sickDoctorOrMedicalUnit: "",
    sickEZlaConfirmed: false,
    sickBenefitPayer: "UNKNOWN",
    sickPayrollPaymentRatePercent: "80.00",
    sickEmployerSickPayDaysUsedBefore: "",
    sickPayDaySequenceStart: "",
    sickPayDaySequenceEnd: "",

    pregnancyExamDate: "",
    pregnancyExamCertificateNumber: "",
    pregnancyExamDoctorOrMedicalUnit: "",
    pregnancyExamMedicalCertificateConfirmed: false,
    pregnancyExamCannotBeDoneOutsideWorkingHours: true,

    adoptionMaternityChildName: "",
    adoptionMaternityChildBirthDate: "",
    adoptionMaternityFinalDate: "",
    adoptionMaternityNumberOfChildren: "1",

    supplementaryMaternityChildName: "",
    supplementaryMaternityChildBirthDate: "",
    supplementaryMaternityPregnancyWeekAtBirth: "",
    supplementaryMaternityBirthWeightGrams: "",
    supplementaryMaternityHospitalizationStartDate: "",
    supplementaryMaternityHospitalizationEndDate: "",
    supplementaryMaternityEntitlementWeeks: "8",
    supplementaryMaternityMedicalCertificateConfirmed: false,

    careAllowanceReasonType: "SICK_CHILD_UNDER_14",
    careAllowanceRecipientName: "",
    careAllowanceRecipientRelation: "CHILD",
    careAllowanceRecipientBirthDate: "",
    careAllowanceCertificateSource: "MANUAL",
    careAllowanceCertificateNumber: "",
    careAllowanceCertificateReceived: false,
    careAllowanceLimitDaysSnapshot: "60",
    careAllowancePayrollPaymentRatePercent: "80.00",

    childcareLongChildName: "",
    childcareLongChildBirthDate: "",
    childcareLongAdoptionFinalDate: "",
    childcareLongEntitlementMonths: "36",
    childcareLongOtherParentDeclaredUsageMonths: "0",
    childcareLongDisabledChildCertificate: false,

    jobSearchNoticePeriodType: "TWO_WEEKS",
    jobSearchEmploymentTerminationDate: "",
    jobSearchEmployerNoticeConfirmed: false,

    // Time-balance settlement mode. AUTO_FIFO remains the default. MANUAL lets
    // HR/employee link this event to a concrete open debt/available credit.
    // NONE deliberately leaves the event open for future compensation.
    timeSettlementMode: TIME_SETTLEMENT_MODE_AUTO,
    // Manual settlement is an allocation ledger proposal, not a simple single
    // target id.  Each row tells the backend which opposite approved request
    // should be compensated and by how many minutes.
    timeSettlementAllocations: [],
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function LeaveRequestCreateModal({
  open,
  onClose,
  currentUserId,
  onCreated,
}) {
  const { t } = useTranslation(["common", "leaves"]);
  const { softLogout, authState } = useContext(AuthContext);

  const isMountedRef = useRef(false);
  const dictAbortRef = useRef(null);
  const cancellableAbortRef = useRef(null);
  const usersAbortRef = useRef(null);
  const settlementAbortRef = useRef(null);

  const [form, setForm] = useState(getInitialFormState());

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [cancellableLeaves, setCancellableLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState(currentUserId ? String(currentUserId) : "");

  const [loading, setLoading] = useState(false);
  const [loadingDict, setLoadingDict] = useState(false);
  const [cancellableLoading, setCancellableLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [settlementCandidates, setSettlementCandidates] = useState([]);
  const [settlementCandidatesLoading, setSettlementCandidatesLoading] = useState(false);
  const [error, setError] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Mounted lifecycle                                                         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (dictAbortRef.current) {
        dictAbortRef.current.abort();
        dictAbortRef.current = null;
      }

      if (cancellableAbortRef.current) {
        cancellableAbortRef.current.abort();
        cancellableAbortRef.current = null;
      }

      if (usersAbortRef.current) {
        usersAbortRef.current.abort();
        usersAbortRef.current = null;
      }

      if (settlementAbortRef.current) {
        settlementAbortRef.current.abort();
        settlementAbortRef.current = null;
      }
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Derived values                                                            */
  /* ------------------------------------------------------------------------ */

  const selectedType = useMemo(() => {
    const id = Number(form.leaveTypeId);
    if (!Number.isFinite(id)) return null;

    return leaveTypes.find((x) => Number(x.id) === id) || null;
  }, [form.leaveTypeId, leaveTypes]);

  const selectedCode = useMemo(
    () => normalizeCode(selectedType?.code),
    [selectedType?.code]
  );

  const entitlementContextKind = useMemo(
    () => getEntitlementContextKind(selectedCode),
    [selectedCode]
  );

  const selectedAccountingType = useMemo(
    () => String(selectedType?.balance_accounting_type || "DEDUCT_FROM_LEAVE_BALANCE").trim().toUpperCase(),
    [selectedType?.balance_accounting_type]
  );

  const selectedUnit = useMemo(
    () => String(selectedType?.unit || "DAYS").trim().toUpperCase(),
    [selectedType?.unit]
  );

  const isCancellation = selectedCode === "CANCELLATION";
  const isHourBasedLeaveType = selectedUnit === "HOURS";
  const isAddTimeDebtEvent = selectedAccountingType === "ADD_TIME_DEBT";
  const isReduceTimeDebtEvent = selectedAccountingType === "REDUCE_TIME_DEBT";
  const isTimeBalanceEvent = isAddTimeDebtEvent || isReduceTimeDebtEvent;
  const timeBalanceHelpKey = isAddTimeDebtEvent
    ? "privateExitToMakeUpHelp"
    : isReduceTimeDebtEvent
      ? "makeUpWorkHelp"
      : null;
  const timeBalanceTitleKey = isAddTimeDebtEvent
    ? "privateExitToMakeUpTitle"
    : isReduceTimeDebtEvent
      ? "makeUpWorkTitle"
      : null;
  const supportsPartialDay = asBool(selectedType?.supports_partial_day);
  const privilegedCreator = isPrivilegedRole(authState?.user?.role);
  const effectiveTargetUserId = privilegedCreator
    ? (targetUserId || (currentUserId ? String(currentUserId) : ""))
    : (currentUserId ? String(currentUserId) : "");

  /**
   * Hours mode is only meaningful for:
   * - non-cancellation types
   * - leave types that explicitly support partial day
   */
  const effectiveHoursMode =
    !isCancellation &&
    supportsPartialDay &&
    form.useHoursMode;

  const cancellationTarget = useMemo(() => {
    if (!form.cancellationTargetId) return null;

    return (
      cancellableLeaves.find(
        (row) => String(row.id) === String(form.cancellationTargetId)
      ) || null
    );
  }, [form.cancellationTargetId, cancellableLeaves]);

  const currentTimeSettlementDurationMinutes = useMemo(() => {
    if (!isTimeBalanceEvent || !effectiveHoursMode) return null;
    return calculateLocalDateTimeDurationMinutes(form.startDT, form.endDT);
  }, [effectiveHoursMode, form.endDT, form.startDT, isTimeBalanceEvent]);

  const selectedTimeSettlementAllocationMinutes = useMemo(() => {
    return (form.timeSettlementAllocations || []).reduce(
      (sum, row) => sum + safePositiveInt(row?.minutes, 0),
      0
    );
  }, [form.timeSettlementAllocations]);

  const remainingTimeSettlementAllocationMinutes = useMemo(() => {
    if (currentTimeSettlementDurationMinutes == null) return null;
    return Math.max(0, currentTimeSettlementDurationMinutes - selectedTimeSettlementAllocationMinutes);
  }, [currentTimeSettlementDurationMinutes, selectedTimeSettlementAllocationMinutes]);

  const entitlementContextComplete = useMemo(() => {
    if (!entitlementContextKind) return true;

    if (entitlementContextKind === "FATHER") {
      if (form.fatherEventType === "ADOPTION") return Boolean(form.fatherAdoptionFinalDate);
      return Boolean(form.fatherChildBirthDate);
    }

    if (entitlementContextKind === "CHILDCARE_188") {
      return Boolean(
        form.childcareChildBirthDate &&
        form.childcareOtherParentDeclarationConfirmed
      );
    }

    if (entitlementContextKind === "CARE_LEAVE") {
      return Boolean(
        form.careRecipientRelation &&
        form.careSeriousMedicalReasonDeclared &&
        (form.careRecipientRelation !== "HOUSEHOLD_MEMBER" || form.careSameHousehold)
      );
    }

    if (entitlementContextKind === "EDUCATION") {
      if (!form.educationReason || !form.educationEmployerConsentConfirmed) return false;

      if (form.educationReason === "DIPLOMA_THESIS_AND_EXAM") {
        return Boolean(form.educationLastYearOfStudies && form.educationDiplomaExamDate);
      }

      if (form.educationReason === "EMPLOYER_APPROVED_OTHER") {
        const n = Number(form.educationCustomEntitlementDays);
        return Number.isFinite(n) && n > 0;
      }

      return Boolean(form.educationExamDate);
    }

    if (entitlementContextKind === "HOLIDAY_COMPENSATION") {
      const minutes = Number(form.holidayCompensationMinutes || 480);
      return Boolean(
        form.holidayDate &&
        form.holidaySettlementPeriodStart &&
        form.holidaySettlementPeriodEnd &&
        Number.isFinite(minutes) &&
        minutes > 0
      );
    }

    if (entitlementContextKind === "BLOOD_DONATION") {
      return Boolean(
        form.bloodDonationDate &&
        form.bloodCertificateReceived &&
        (form.bloodMedicalExamOnly || form.bloodDonationSuccessful)
      );
    }

    if (entitlementContextKind === "MATERNITY") {
      const childCount = Number(form.maternityNumberOfChildrenAtBirth || 1);
      return Boolean(
        (form.maternityExpectedBirthDate || form.maternityChildBirthDate) &&
        Number.isFinite(childCount) &&
        childCount >= 1
      );
    }

    if (entitlementContextKind === "PARENTAL") {
      const childCount = Number(form.parentalNumberOfChildrenAtBirth || 1);
      const customRate = Number(form.parentalCustomPaymentRatePercent);
      const hasEventDate = form.parentalEventType === "ADOPTION"
        ? Boolean(form.parentalAdoptionFinalDate)
        : Boolean(form.parentalChildBirthDate);

      return Boolean(
        hasEventDate &&
        Number.isFinite(childCount) &&
        childCount >= 1 &&
        (form.parentalLeaveVariant !== "CUSTOM" || (Number.isFinite(customRate) && customRate >= 0 && customRate <= 100))
      );
    }

    if (entitlementContextKind === "SICK_LEAVE") {
      const rate = Number(form.sickPayrollPaymentRatePercent);
      return Boolean(
        form.startDate &&
        form.endDateInclusive &&
        form.sickReasonType &&
        form.sickCertificateSource &&
        Number.isFinite(rate) &&
        rate >= 0 &&
        rate <= 100
      );
    }

    if (entitlementContextKind === "PREGNANCY_MEDICAL_EXAM") {
      return Boolean(form.pregnancyExamDate);
    }

    if (entitlementContextKind === "ADOPTION_MATERNITY") {
      const childCount = Number(form.adoptionMaternityNumberOfChildren || 1);
      return Boolean(
        form.adoptionMaternityFinalDate &&
        Number.isFinite(childCount) &&
        childCount >= 1
      );
    }

    if (entitlementContextKind === "SUPPLEMENTARY_MATERNITY") {
      const weeks = Number(form.supplementaryMaternityEntitlementWeeks || 0);
      return Boolean(
        form.supplementaryMaternityChildBirthDate &&
        form.supplementaryMaternityHospitalizationStartDate &&
        form.supplementaryMaternityHospitalizationEndDate &&
        Number.isFinite(weeks) &&
        weeks > 0 &&
        weeks <= 15
      );
    }

    if (entitlementContextKind === "CARE_ALLOWANCE") {
      const rate = Number(form.careAllowancePayrollPaymentRatePercent);
      return Boolean(
        form.careAllowanceReasonType &&
        form.careAllowanceRecipientRelation &&
        form.careAllowanceCertificateSource &&
        Number.isFinite(rate) &&
        rate >= 0 &&
        rate <= 100
      );
    }

    if (entitlementContextKind === "CHILDCARE_LONG") {
      return Boolean(form.childcareLongChildBirthDate || form.childcareLongAdoptionFinalDate);
    }

    if (entitlementContextKind === "JOB_SEARCH") {
      return Boolean(
        form.jobSearchNoticePeriodType &&
        form.jobSearchEmploymentTerminationDate &&
        form.jobSearchEmployerNoticeConfirmed
      );
    }

    return true;
  }, [entitlementContextKind, form]);

  const canSubmit = useMemo(() => {
    if (loading || loadingDict || loadingUsers) return false;
    if (!effectiveTargetUserId) return false;
    if (!form.leaveTypeId) return false;

    if (isCancellation) {
      return Boolean(form.cancellationTargetId);
    }

    if (!entitlementContextComplete) return false;

    if (effectiveHoursMode) {
      return Boolean(form.startDT && form.endDT);
    }

    return Boolean(form.startDate && form.endDateInclusive);
  }, [loading, loadingDict, loadingUsers, effectiveTargetUserId, form, isCancellation, effectiveHoursMode, entitlementContextComplete]);

  /* ------------------------------------------------------------------------ */
  /* Small form helpers                                                        */
  /* ------------------------------------------------------------------------ */

  function updateFormField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function resetFormState() {
    setForm(getInitialFormState());
    setError("");
    setLoading(false);
    setLoadingDict(false);
    setCancellableLoading(false);
    setLoadingUsers(false);
    setLeaveTypes([]);
    setApprovers([]);
    setCancellableLeaves([]);
    setUsers([]);
    setTargetUserId(currentUserId ? String(currentUserId) : "");
  }

  function closeAndReset() {
    onClose?.();
    resetFormState();
  }

  /* ------------------------------------------------------------------------ */
  /* Reset when modal closes externally                                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (open) return;

    if (dictAbortRef.current) {
      dictAbortRef.current.abort();
      dictAbortRef.current = null;
    }

    if (cancellableAbortRef.current) {
      cancellableAbortRef.current.abort();
      cancellableAbortRef.current = null;
    }

    if (usersAbortRef.current) {
      usersAbortRef.current.abort();
      usersAbortRef.current = null;
    }

    resetFormState();
  }, [open]);

  /* ------------------------------------------------------------------------ */
  /* ESC close + body scroll lock                                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        closeAndReset();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, loading]);

  /* ------------------------------------------------------------------------ */
  /* Load employees for privileged on-behalf request creation                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;

    const selfId = currentUserId ? String(currentUserId) : "";
    setTargetUserId((prev) => prev || selfId);

    if (!privilegedCreator) {
      setUsers([]);
      setTargetUserId(selfId);
      return;
    }

    if (usersAbortRef.current) {
      usersAbortRef.current.abort();
    }

    const controller = new AbortController();
    usersAbortRef.current = controller;

    const run = async () => {
      setLoadingUsers(true);

      try {
        const rows = await fetchUsers({ signal: controller.signal });
        if (controller.signal.aborted || !isMountedRef.current) return;

        const safeRows = Array.isArray(rows) ? rows : [];
        setUsers(safeRows);

        setTargetUserId((prev) => {
          if (prev && safeRows.some((u) => String(u.id) === String(prev))) return String(prev);
          if (selfId && safeRows.some((u) => String(u.id) === String(selfId))) return selfId;
          return safeRows[0]?.id ? String(safeRows[0].id) : selfId;
        });
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout("LEAVE_MODAL_USERS_LOAD_AUTH_FAILURE");
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn("[LeaveRequestCreateModal] users load failed:", err);
        }

        if (!isMountedRef.current) return;
        setUsers([]);
        setError(getTranslatedApiErrorMessage(err, t, t("leaves:modal.loadUsersError", { defaultValue: "Failed to load employee list." })));
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setLoadingUsers(false);
        }
      }
    };

    run();

    return () => controller.abort();
  }, [open, privilegedCreator, currentUserId, softLogout, t]);

  /* ------------------------------------------------------------------------ */
  /* Load dictionaries on open                                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;
    if (!effectiveTargetUserId) {
      setError(t("leaves:modal.missingCurrentUser", { defaultValue: "Cannot open leave request creator: missing employee id." }));
      return;
    }

    if (dictAbortRef.current) {
      dictAbortRef.current.abort();
    }

    const controller = new AbortController();
    dictAbortRef.current = controller;

    const run = async () => {
      setError("");
      setLoadingDict(true);

      try {
        const bundle = await fetchVacationSettings(effectiveTargetUserId, {
          signal: controller.signal,
        });

        if (controller.signal.aborted || !isMountedRef.current) return;

        const sortedLeaveTypes = sortLeaveTypes(
          bundle?.leaveTypes || bundle?.leave_types || []
        );

        setLeaveTypes(sortedLeaveTypes);

        // Preserve existing choice only if it still exists in the freshly loaded set.
        setForm((prev) => {
          const hasExisting = sortedLeaveTypes.some(
            (lt) => String(lt.id) === String(prev.leaveTypeId)
          );

          const today = toLocalISODate(new Date());

          return {
            ...prev,
            leaveTypeId: hasExisting
              ? prev.leaveTypeId
              : sortedLeaveTypes[0]
              ? String(sortedLeaveTypes[0].id)
              : "",
            startDate: prev.startDate || today,
            endDateInclusive: prev.endDateInclusive || today,
            // Do not eagerly initialize hour-based datetime values here.
            // The selected leave type decides whether hours mode is relevant,
            // and the dedicated effect below chooses type-specific defaults
            // (for example 10:00 for private exits and 16:00 for make-up work).
            startDT: prev.startDT || "",
            endDT: prev.endDT || "",
          };
        });

        try {
          const approverRows = await fetchLeaveApprovers({
            signal: controller.signal,
          });

          if (controller.signal.aborted || !isMountedRef.current) return;
          setApprovers(Array.isArray(approverRows) ? approverRows : []);
        } catch (err) {
          if (isRequestCanceled(err) || controller.signal.aborted) return;

          if (isAuthFailure(err)) {
            softLogout("LEAVE_MODAL_APPROVER_LOAD_AUTH_FAILURE");
            return;
          }

          if (DEV) {
            // eslint-disable-next-line no-console
            console.warn("[LeaveRequestCreateModal] approvers load failed:", err);
          }

          setApprovers([]);
        }
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout("LEAVE_MODAL_DICT_LOAD_AUTH_FAILURE");
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.error("[LeaveRequestCreateModal] dictionary load failed:", err);
        }

        if (!isMountedRef.current) return;
        setError(getTranslatedApiErrorMessage(err, t, t("leaves:modal.loadTypesError", { defaultValue: "Failed to load leave types." })));
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setLoadingDict(false);
        }
      }
    };

    run();

    return () => controller.abort();
  }, [open, effectiveTargetUserId, softLogout, t]);

  /* ------------------------------------------------------------------------ */
  /* Enforce mode rules when leave type changes                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;

    setForm((prev) => {
      let next = prev;

      // Cancellation requests should never stay in draft mode.
      if (isCancellation && prev.draft) {
        next = { ...next, draft: false };
      }

      // Time-balance events such as PRIVATE_EXIT_TO_MAKE_UP and MAKE_UP_WORK
      // are operationally hour-based in HB.  If the leave type supports partial
      // day mode, default to precise datetime input so employees do not
      // accidentally create full-day time-debt events.
      if ((isTimeBalanceEvent || isHourBasedLeaveType) && supportsPartialDay && !prev.useHoursMode) {
        next = { ...next, useHoursMode: true };
      }

      // Hours mode must be disabled if no longer valid.
      if ((isCancellation || !supportsPartialDay) && prev.useHoursMode) {
        next = { ...next, useHoursMode: false };
      }

      // Ensure day-based defaults exist.
      const today = toLocalISODate(new Date());
      if (!next.startDate) next = { ...next, startDate: today };
      if (!next.endDateInclusive) next = { ...next, endDateInclusive: today };

      return next;
    });
  }, [open, isCancellation, isTimeBalanceEvent, isHourBasedLeaveType, supportsPartialDay]);

  /* ------------------------------------------------------------------------ */
  /* Initialize sensible hours defaults when hours mode becomes active         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;
    if (!effectiveHoursMode) return;

    setForm((prev) => {
      const baseDate = prev.startDate || toLocalISODate(new Date());

      /**
       * Time-balance events are operationally short, precise time intervals.
       * The previous generic 09:00-17:00 default was safe for full-day absence
       * thinking, but it made private exits and make-up work look like an
       * accidental 8-hour event.  For ADD_TIME_DEBT / REDUCE_TIME_DEBT we start
       * with a one-hour interval and let the user adjust the exact timestamps.
       */
      const genericStart = `${baseDate}T09:00`;
      const genericFullDayEnd = `${baseDate}T17:00`;
      const timeDebtStart = isAddTimeDebtEvent ? `${baseDate}T10:00` : `${baseDate}T16:00`;
      const timeDebtEnd = isAddTimeDebtEvent ? `${baseDate}T11:00` : `${baseDate}T17:00`;

      const defaultStart = isTimeBalanceEvent ? timeDebtStart : genericStart;
      const defaultEnd = isTimeBalanceEvent ? timeDebtEnd : genericFullDayEnd;

      const shouldReplaceGenericHours =
        isTimeBalanceEvent &&
        (!prev.startDT || !prev.endDT || prev.endDT === genericFullDayEnd);

      return {
        ...prev,
        startDT: shouldReplaceGenericHours ? defaultStart : (prev.startDT || defaultStart),
        endDT: shouldReplaceGenericHours ? defaultEnd : (prev.endDT || defaultEnd),
      };
    });
  }, [open, effectiveHoursMode, isTimeBalanceEvent, isAddTimeDebtEvent]);

  /* ------------------------------------------------------------------------ */
  /* Load cancellable leaves only when needed                                  */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;
    if (!isCancellation) {
      // Only clear cancellation-specific state here.
      // Do NOT clear the employee list or reset targetUserId: this effect also
      // re-runs after a privileged user selects another employee because
      // effectiveTargetUserId is part of the dependency list. Resetting targetUserId
      // here would immediately jump the dropdown back to the current superuser and
      // make on-behalf request creation impossible for all non-cancellation leave
      // types, including SICK_LEAVE / L4.
      setCancellableLeaves([]);
      setForm((prev) => ({ ...prev, cancellationTargetId: "" }));
      return;
    }

    if (cancellableAbortRef.current) {
      cancellableAbortRef.current.abort();
    }

    const controller = new AbortController();
    cancellableAbortRef.current = controller;

    const run = async () => {
      setCancellableLoading(true);
      setError("");

      try {
        const list = await listCancellableLeaves({
          userId: privilegedCreator ? effectiveTargetUserId : undefined,
          signal: controller.signal,
        });

        if (controller.signal.aborted || !isMountedRef.current) return;

        const safeList = Array.isArray(list) ? list : [];
        setCancellableLeaves(safeList);

        setForm((prev) => {
          const hasExisting = safeList.some(
            (row) => String(row.id) === String(prev.cancellationTargetId)
          );

          return {
            ...prev,
            cancellationTargetId: hasExisting
              ? prev.cancellationTargetId
              : safeList[0]?.id
              ? String(safeList[0].id)
              : "",
          };
        });
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout("LEAVE_MODAL_CANCELLABLE_LOAD_AUTH_FAILURE");
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.error("[LeaveRequestCreateModal] listCancellableLeaves failed:", err);
        }

        if (!isMountedRef.current) return;
        setError(getTranslatedApiErrorMessage(err, t, t("leaves:modal.loadCancellableError", { defaultValue: "Failed to load cancellable requests." })));
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setCancellableLoading(false);
        }
      }
    };

    run();

    return () => controller.abort();
  }, [open, isCancellation, privilegedCreator, effectiveTargetUserId, softLogout, t]);

  /* ------------------------------------------------------------------------ */
  /* Load time-settlement candidates for the selected employee/type              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) return;

    if (!isTimeBalanceEvent || !form.leaveTypeId || !effectiveTargetUserId) {
      setSettlementCandidates([]);
      setForm((prev) => ({
        ...prev,
        timeSettlementMode: TIME_SETTLEMENT_MODE_AUTO,
        timeSettlementAllocations: [],
      }));
      return;
    }

    if (settlementAbortRef.current) {
      settlementAbortRef.current.abort();
    }

    const controller = new AbortController();
    settlementAbortRef.current = controller;

    const run = async () => {
      setSettlementCandidatesLoading(true);

      try {
        const result = await fetchTimeSettlementCandidates({
          leaveTypeId: form.leaveTypeId,
          targetUserId: effectiveTargetUserId,
          limit: 100,
          signal: controller.signal,
        });

        if (controller.signal.aborted || !isMountedRef.current) return;

        const candidates = Array.isArray(result?.candidates) ? result.candidates : [];
        setSettlementCandidates(candidates);

        setForm((prev) => {
          const availableIds = new Set(
            candidates.map((row) => getSettlementCandidateId(row)).filter(Boolean)
          );
          const nextAllocations = (prev.timeSettlementAllocations || []).filter((row) =>
            availableIds.has(String(row.target_leave_request_id || ""))
          );

          if (nextAllocations.length === (prev.timeSettlementAllocations || []).length) {
            return prev;
          }

          return {
            ...prev,
            timeSettlementAllocations: nextAllocations,
          };
        });
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout("LEAVE_MODAL_TIME_SETTLEMENT_CANDIDATES_AUTH_FAILURE");
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn("[LeaveRequestCreateModal] time-settlement candidates load failed:", err);
        }

        if (!isMountedRef.current) return;
        setSettlementCandidates([]);
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setSettlementCandidatesLoading(false);
        }
      }
    };

    run();

    return () => controller.abort();
  }, [open, isTimeBalanceEvent, form.leaveTypeId, effectiveTargetUserId, softLogout]);

  /* ------------------------------------------------------------------------ */
  /* Entitlement context payload builder                                        */
  /* ------------------------------------------------------------------------ */

  function buildEntitlementContextPayload() {
    if (entitlementContextKind === "FATHER") {
      return {
        event_type: form.fatherEventType || "BIRTH",
        child_name: form.fatherChildName?.trim() || null,
        child_birth_date: form.fatherChildBirthDate || null,
        adoption_final_date: form.fatherAdoptionFinalDate || null,
      };
    }

    if (entitlementContextKind === "CHILDCARE_188") {
      return {
        child_name: form.childcareChildName?.trim() || null,
        child_birth_date: form.childcareChildBirthDate || null,
        other_parent_declaration_confirmed: Boolean(
          form.childcareOtherParentDeclarationConfirmed
        ),
      };
    }

    if (entitlementContextKind === "CARE_LEAVE") {
      return {
        recipient_name: form.careRecipientName?.trim() || null,
        recipient_relation: form.careRecipientRelation || null,
        same_household: Boolean(form.careSameHousehold),
        serious_medical_reason_declared: Boolean(
          form.careSeriousMedicalReasonDeclared
        ),
      };
    }

    if (entitlementContextKind === "EDUCATION") {
      const customDays = Number(form.educationCustomEntitlementDays);

      return {
        education_reason: form.educationReason || null,
        institution_name: form.educationInstitutionName?.trim() || null,
        study_program_name: form.educationStudyProgramName?.trim() || null,
        exam_date: form.educationExamDate || null,
        diploma_exam_date: form.educationDiplomaExamDate || null,
        last_year_of_studies: Boolean(form.educationLastYearOfStudies),
        employer_consent_confirmed: Boolean(form.educationEmployerConsentConfirmed),
        entitlement_days: Number.isFinite(customDays) ? customDays : null,
      };
    }

    if (entitlementContextKind === "HOLIDAY_COMPENSATION") {
      const minutes = Number(form.holidayCompensationMinutes || 480);

      return {
        holiday_date: form.holidayDate || null,
        holiday_name: form.holidayName?.trim() || null,
        settlement_period_start: form.holidaySettlementPeriodStart || null,
        settlement_period_end: form.holidaySettlementPeriodEnd || null,
        compensation_minutes: Number.isFinite(minutes) ? Math.round(minutes) : 480,
        must_be_taken_until: form.holidayMustBeTakenUntil || null,
      };
    }

    if (entitlementContextKind === "BLOOD_DONATION") {
      return {
        donation_date: form.bloodDonationDate || null,
        donation_center_name: form.bloodDonationCenterName?.trim() || null,
        certificate_number: form.bloodCertificateNumber?.trim() || null,
        certificate_received: Boolean(form.bloodCertificateReceived),
        donation_successful: Boolean(form.bloodDonationSuccessful),
        includes_next_day_off: Boolean(form.bloodIncludesNextDayOff),
        medical_exam_only: Boolean(form.bloodMedicalExamOnly),
      };
    }

    if (entitlementContextKind === "MATERNITY") {
      const childCount = Number(form.maternityNumberOfChildrenAtBirth || 1);

      return {
        event_type: "BIRTH",
        expected_birth_date: form.maternityExpectedBirthDate || null,
        child_birth_date: form.maternityChildBirthDate || null,
        number_of_children_at_birth: Number.isFinite(childCount) ? Math.max(1, Math.trunc(childCount)) : 1,
        pre_birth_leave_start_date: form.maternityPreBirthLeaveStartDate || null,
        maternity_leave_variant: form.maternityLeaveVariant || "STANDARD",
      };
    }

    if (entitlementContextKind === "PARENTAL") {
      const childCount = Number(form.parentalNumberOfChildrenAtBirth || 1);
      const otherParentWeeks = Number(form.parentalOtherParentDeclaredUsageWeeks || 0);
      const workFraction = Number(form.parentalWorkFractionDuringLeave);
      const extendedWeeks = Number(form.parentalExtendedEntitlementWeeks);
      const customRate = Number(form.parentalCustomPaymentRatePercent);

      return {
        event_type: form.parentalEventType || "BIRTH",
        child_name: form.parentalChildName?.trim() || null,
        child_birth_date: form.parentalChildBirthDate || null,
        adoption_final_date: form.parentalAdoptionFinalDate || null,
        number_of_children_at_birth: Number.isFinite(childCount) ? Math.max(1, Math.trunc(childCount)) : 1,
        has_za_zyciem_certificate: Boolean(form.parentalHasZaZyciemCertificate),
        parental_leave_variant: form.parentalLeaveVariant || "STANDARD_70",
        payroll_payment_rate_percent: Number.isFinite(customRate) ? customRate : null,
        other_parent_declared_usage_weeks: Number.isFinite(otherParentWeeks) ? Math.max(0, otherParentWeeks) : 0,
        own_non_transferable_part_used: Boolean(form.parentalOwnNonTransferablePartUsed),
        combined_with_work: Boolean(form.parentalCombinedWithWork),
        work_fraction_during_leave: Number.isFinite(workFraction) ? workFraction : null,
        extended_entitlement_weeks: Number.isFinite(extendedWeeks) ? extendedWeeks : null,
      };
    }

    if (entitlementContextKind === "SICK_LEAVE") {
      const rate = Number(form.sickPayrollPaymentRatePercent);

      return {
        sick_leave_reason_type: form.sickReasonType || "STANDARD",
        certificate_source: form.sickCertificateSource || "MANUAL",
        certificate_number: form.sickCertificateNumber?.trim() || null,
        incapacity_start_date: form.startDate || null,
        incapacity_end_date: form.endDateInclusive || null,
        issue_date: form.sickIssueDate || null,
        received_at: form.sickReceivedAt ? `${form.sickReceivedAt}T00:00:00` : null,
        doctor_or_medical_unit: form.sickDoctorOrMedicalUnit?.trim() || null,
        is_e_zla_confirmed: Boolean(form.sickEZlaConfirmed),
        benefit_payer: form.sickBenefitPayer || "UNKNOWN",
        payroll_payment_rate_percent: Number.isFinite(rate) ? rate : null,
        employer_sick_pay_days_used_before: toNullableInt(form.sickEmployerSickPayDaysUsedBefore),
        sick_pay_day_sequence_start: toNullableInt(form.sickPayDaySequenceStart),
        sick_pay_day_sequence_end: toNullableInt(form.sickPayDaySequenceEnd),
      };
    }

    if (entitlementContextKind === "PREGNANCY_MEDICAL_EXAM") {
      return {
        exam_date: form.pregnancyExamDate || null,
        certificate_number: form.pregnancyExamCertificateNumber?.trim() || null,
        doctor_or_medical_unit: form.pregnancyExamDoctorOrMedicalUnit?.trim() || null,
        medical_certificate_confirmed: Boolean(form.pregnancyExamMedicalCertificateConfirmed),
        cannot_be_done_outside_working_hours: Boolean(form.pregnancyExamCannotBeDoneOutsideWorkingHours),
        payroll_payment_rate_percent: 100,
      };
    }

    if (entitlementContextKind === "ADOPTION_MATERNITY") {
      const childCount = Number(form.adoptionMaternityNumberOfChildren || 1);
      return {
        event_type: "ADOPTION",
        child_name: form.adoptionMaternityChildName?.trim() || null,
        child_birth_date: form.adoptionMaternityChildBirthDate || null,
        adoption_final_date: form.adoptionMaternityFinalDate || null,
        number_of_children_at_birth: Number.isFinite(childCount) ? Math.max(1, Math.trunc(childCount)) : 1,
        payroll_payment_rate_percent: 100,
      };
    }

    if (entitlementContextKind === "SUPPLEMENTARY_MATERNITY") {
      const pregnancyWeek = Number(form.supplementaryMaternityPregnancyWeekAtBirth);
      const birthWeight = Number(form.supplementaryMaternityBirthWeightGrams);
      const entitlementWeeks = Number(form.supplementaryMaternityEntitlementWeeks || 8);
      return {
        event_type: "SUPPLEMENTARY_MATERNITY",
        child_name: form.supplementaryMaternityChildName?.trim() || null,
        child_birth_date: form.supplementaryMaternityChildBirthDate || null,
        pregnancy_week_at_birth: Number.isFinite(pregnancyWeek) ? Math.max(0, Math.trunc(pregnancyWeek)) : null,
        birth_weight_grams: Number.isFinite(birthWeight) ? Math.max(0, Math.trunc(birthWeight)) : null,
        hospitalization_start_date: form.supplementaryMaternityHospitalizationStartDate || null,
        hospitalization_end_date: form.supplementaryMaternityHospitalizationEndDate || null,
        supplementary_maternity_entitlement_weeks: Number.isFinite(entitlementWeeks) ? Math.max(1, Math.trunc(entitlementWeeks)) : 8,
        medical_certificate_confirmed: Boolean(form.supplementaryMaternityMedicalCertificateConfirmed),
        payroll_payment_rate_percent: 100,
      };
    }

    if (entitlementContextKind === "CARE_ALLOWANCE") {
      const rate = Number(form.careAllowancePayrollPaymentRatePercent);
      const limit = Number(form.careAllowanceLimitDaysSnapshot);
      return {
        care_allowance_reason_type: form.careAllowanceReasonType || "SICK_CHILD_UNDER_14",
        recipient_name: form.careAllowanceRecipientName?.trim() || null,
        recipient_relation: form.careAllowanceRecipientRelation || null,
        recipient_birth_date: form.careAllowanceRecipientBirthDate || null,
        certificate_source: form.careAllowanceCertificateSource || "MANUAL",
        certificate_number: form.careAllowanceCertificateNumber?.trim() || null,
        certificate_received: Boolean(form.careAllowanceCertificateReceived),
        care_allowance_limit_days_snapshot: Number.isFinite(limit) ? Math.max(0, Math.trunc(limit)) : null,
        payroll_payment_rate_percent: Number.isFinite(rate) ? rate : 80,
      };
    }

    if (entitlementContextKind === "CHILDCARE_LONG") {
      const entitlementMonths = Number(form.childcareLongEntitlementMonths || 36);
      const otherParentMonths = Number(form.childcareLongOtherParentDeclaredUsageMonths || 0);
      return {
        child_name: form.childcareLongChildName?.trim() || null,
        child_birth_date: form.childcareLongChildBirthDate || null,
        adoption_final_date: form.childcareLongAdoptionFinalDate || null,
        childcare_leave_long_entitlement_months: Number.isFinite(entitlementMonths) ? Math.max(1, Math.trunc(entitlementMonths)) : 36,
        other_parent_declared_usage_months: Number.isFinite(otherParentMonths) ? Math.max(0, otherParentMonths) : 0,
        disabled_child_certificate: Boolean(form.childcareLongDisabledChildCertificate),
        payroll_payment_rate_percent: 0,
      };
    }

    if (entitlementContextKind === "JOB_SEARCH") {
      return {
        notice_period_type: form.jobSearchNoticePeriodType || "TWO_WEEKS",
        employment_termination_date: form.jobSearchEmploymentTerminationDate || null,
        employer_notice_confirmed: Boolean(form.jobSearchEmployerNoticeConfirmed),
        payroll_payment_rate_percent: 100,
      };
    }

    return null;
  }

  function getManualSettlementAllocation(candidateId) {
    const id = String(candidateId || "");
    return (form.timeSettlementAllocations || []).find(
      (row) => String(row.target_leave_request_id || "") === id
    ) || null;
  }

  function setManualSettlementAllocation(candidateId, minutesValue) {
    const id = String(candidateId || "").trim();
    if (!id) return;

    setForm((prev) => {
      const current = Array.isArray(prev.timeSettlementAllocations)
        ? prev.timeSettlementAllocations
        : [];
      const nextValue = minutesValue == null ? "" : String(minutesValue);
      const exists = current.some((row) => String(row.target_leave_request_id || "") === id);
      const next = exists
        ? current.map((row) =>
            String(row.target_leave_request_id || "") === id
              ? { ...row, minutes: nextValue }
              : row
          )
        : [...current, { target_leave_request_id: id, minutes: nextValue }];

      return { ...prev, timeSettlementAllocations: next };
    });
  }

  function removeManualSettlementAllocation(candidateId) {
    const id = String(candidateId || "").trim();
    if (!id) return;

    setForm((prev) => ({
      ...prev,
      timeSettlementAllocations: (prev.timeSettlementAllocations || []).filter(
        (row) => String(row.target_leave_request_id || "") !== id
      ),
    }));
  }

  function toggleManualSettlementCandidate(candidate, checked) {
    const id = getSettlementCandidateId(candidate);
    if (!id) return;

    if (!checked) {
      removeManualSettlementAllocation(id);
      return;
    }

    const candidateRemaining = getCandidateRemainingMinutes(candidate);
    const alreadyAllocatedElsewhere = (form.timeSettlementAllocations || []).reduce((sum, row) => {
      if (String(row.target_leave_request_id || "") === String(id)) return sum;
      return sum + safePositiveInt(row?.minutes, 0);
    }, 0);
    const remainingForNewRequest = currentTimeSettlementDurationMinutes == null
      ? candidateRemaining
      : Math.max(0, currentTimeSettlementDurationMinutes - alreadyAllocatedElsewhere);
    const suggestedMinutes = Math.min(candidateRemaining, remainingForNewRequest);

    if (suggestedMinutes <= 0) {
      setManualSettlementAllocation(id, "");
      return;
    }

    setManualSettlementAllocation(id, String(suggestedMinutes));
  }

  function buildTimeSettlementPayload() {
    if (!isTimeBalanceEvent) return null;

    const mode = form.timeSettlementMode || TIME_SETTLEMENT_MODE_AUTO;
    if (mode === TIME_SETTLEMENT_MODE_NONE) {
      return { mode: TIME_SETTLEMENT_MODE_NONE, allocations: [] };
    }

    if (mode !== TIME_SETTLEMENT_MODE_MANUAL) {
      return { mode: TIME_SETTLEMENT_MODE_AUTO, allocations: [] };
    }

    const candidateById = new Map(
      (settlementCandidates || [])
        .map((candidate) => [getSettlementCandidateId(candidate), candidate])
        .filter(([id]) => Boolean(id))
    );

    const allocations = (form.timeSettlementAllocations || [])
      .map((row) => {
        const targetId = String(row?.target_leave_request_id || "").trim();
        const minutes = safePositiveInt(row?.minutes, 0);
        return { target_leave_request_id: targetId, minutes };
      })
      .filter((row) => row.target_leave_request_id && row.minutes > 0);

    if (!allocations.length) {
      throw new Error(t("leaves:modal.timeBalance.selectSettlementTarget", { defaultValue: "Select at least one time-balance entry to compensate or switch back to automatic FIFO." }));
    }

    let totalMinutes = 0;
    for (const allocation of allocations) {
      const candidate = candidateById.get(allocation.target_leave_request_id);
      if (!candidate) {
        throw new Error(t("leaves:modal.timeBalance.selectedCandidateUnavailable", { defaultValue: "One selected settlement entry is no longer available. Please refresh the selection." }));
      }

      const remaining = getCandidateRemainingMinutes(candidate);
      if (allocation.minutes > remaining) {
        throw new Error(t("leaves:modal.timeBalance.allocationExceedsCandidate", { defaultValue: "One allocation exceeds the currently open minutes of the selected entry." }));
      }

      totalMinutes += allocation.minutes;
    }

    if (currentTimeSettlementDurationMinutes != null && totalMinutes > currentTimeSettlementDurationMinutes) {
      throw new Error(t("leaves:modal.timeBalance.allocationExceedsRequest", { defaultValue: "Manual settlement cannot exceed the duration of the new request." }));
    }

    return {
      mode: TIME_SETTLEMENT_MODE_MANUAL,
      allocations,
    };
  }

  /* ------------------------------------------------------------------------ */
  /* Payload builder                                                           */
  /* ------------------------------------------------------------------------ */

  function buildPayload() {
    const leaveTypeIdNum = Number(form.leaveTypeId);
    if (!Number.isFinite(leaveTypeIdNum) || leaveTypeIdNum <= 0) {
      throw new Error(t("leaves:modal.selectLeaveType", { defaultValue: "Select leave type." }));
    }

    const approverPart =
      form.approverId !== AUTO_APPROVER_VALUE
        ? { approver_user_id: Number(form.approverId) }
        : {};

    /* ---------------------------------------------------------------------- */
    /* Cancellation payload                                                    */
    /* ---------------------------------------------------------------------- */
    if (isCancellation) {
      if (!form.cancellationTargetId) {
        throw new Error(t("leaves:modal.selectRequestToCancel", { defaultValue: "Select a request to cancel." }));
      }

      return {
        leave_type_id: leaveTypeIdNum,
        target_user_id: Number(effectiveTargetUserId),
        cancellation_of_id: String(form.cancellationTargetId),
        note: form.note?.trim() ? form.note.trim() : null,
        draft: false,
        ...approverPart,
      };
    }

    /* ---------------------------------------------------------------------- */
    /* Hours mode                                                              */
    /* ---------------------------------------------------------------------- */
    if (effectiveHoursMode) {
      const start = new Date(form.startDT);
      const end = new Date(form.endDT);

      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
        throw new Error(t("leaves:modal.invalidDatetime", { defaultValue: "Invalid start/end datetime." }));
      }

      if (end <= start) {
        throw new Error(t("leaves:modal.endAfterStart", { defaultValue: "End must be after start." }));
      }

      const entitlementContext = buildEntitlementContextPayload();

      return {
        leave_type_id: leaveTypeIdNum,
        target_user_id: Number(effectiveTargetUserId),
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        requested_usage_mode: "HOURS",
        ...(entitlementContext ? { entitlement_context: entitlementContext } : {}),
        ...(isTimeBalanceEvent ? { time_settlement: buildTimeSettlementPayload() } : {}),
        note: form.note?.trim() ? form.note.trim() : null,
        draft: Boolean(form.draft),
        ...approverPart,
      };
    }

    /* ---------------------------------------------------------------------- */
    /* Days mode (default)                                                     */
    /* ---------------------------------------------------------------------- */
    if (!form.startDate || !form.endDateInclusive) {
      throw new Error(t("leaves:modal.datesRequired", { defaultValue: "Start date and end date are required." }));
    }

    const endExclusive = addDaysLocal(form.endDateInclusive, 1);

    const start = new Date(`${form.startDate}T00:00:00`);
    const end = new Date(`${endExclusive}T00:00:00`);

    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
      throw new Error(t("leaves:modal.invalidDateRange", { defaultValue: "Invalid date range." }));
    }

    if (end <= start) {
      throw new Error(t("leaves:modal.endDateAfterStartDate", { defaultValue: "End date must be same or after start date." }));
    }

    const entitlementContext = buildEntitlementContextPayload();

    return {
      leave_type_id: leaveTypeIdNum,
      target_user_id: Number(effectiveTargetUserId),
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      requested_usage_mode: "DAYS",
      ...(entitlementContext ? { entitlement_context: entitlementContext } : {}),
      ...(isTimeBalanceEvent ? { time_settlement: buildTimeSettlementPayload() } : {}),
      note: form.note?.trim() ? form.note.trim() : null,
      draft: Boolean(form.draft),
      ...approverPart,
    };
  }

  /* ------------------------------------------------------------------------ */
  /* Submit                                                                    */
  /* ------------------------------------------------------------------------ */

  async function onSubmit() {
    setError("");
    setLoading(true);

    try {
      const payload = buildPayload();
      const response = await createLeaveRequest(payload);

      onCreated?.(response);
      closeAndReset();
    } catch (err) {
      if (isAuthFailure(err)) {
        softLogout("LEAVE_MODAL_CREATE_AUTH_FAILURE");
        return;
      }

      if (DEV) {
        // eslint-disable-next-line no-console
        console.error("[LeaveRequestCreateModal] create failed:", err);
      }

      if (!isMountedRef.current) return;
      setError(getTranslatedApiErrorMessage(err, t, t("leaves:modal.createError", { defaultValue: "Failed to create leave request." })));
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  if (!open) return null;

  return (
    <div
      className="lrcm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("leaves:modal.ariaLabel", { defaultValue: "Create leave request" })}
      onClick={() => {
        if (!loading) closeAndReset();
      }}
    >
      <div className="lrcm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lrcm-header">
          <div>
            <div className="lrcm-title">{t("leaves:modal.title")}</div>
            <div className="lrcm-sub">{t("leaves:modal.subtitle")}</div>
          </div>

          <button
            className="lrcm-btn lrcm-btn--ghost"
            type="button"
            onClick={closeAndReset}
            disabled={loading}
            aria-label={t("common:actions.close")}
            title={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        <div className="lrcm-help" style={{ padding: "12px 16px 0" }}>
          {t("leaves:modal.helpWorkItem")}
        </div>

        {error && <div className="lrcm-alert lrcm-alert--error">{error}</div>}

        {loadingDict ? (
          <div style={{ padding: 16, color: "#6b7280" }}>{t("leaves:modal.loadTypes")}</div>
        ) : (
          <>
            <div className="lrcm-grid">
              {privilegedCreator && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.targetEmployee", { defaultValue: "Employee" })}</label>
                  <select
                    value={effectiveTargetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    disabled={loading || loadingUsers}
                  >
                    {(users.length ? users : [{ id: currentUserId, username: authState?.user?.username }]).map((user) => (
                      <option key={user.id} value={user.id}>
                        {formatEmployeeOption(user)}
                      </option>
                    ))}
                  </select>
                  <div className="lrcm-help">
                    {loadingUsers
                      ? t("leaves:modal.loadUsers", { defaultValue: "Loading employees…" })
                      : t("leaves:modal.targetEmployeeHelp", { defaultValue: "Superusers can create the request on behalf of another employee. The backend calculates balances and schedules for the selected employee." })}
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------------------- */}
              {/* Leave type                                                        */}
              {/* ---------------------------------------------------------------- */}
              <div className="lrcm-field">
                <label>{t("leaves:common.leaveType")}</label>
                <LeaveTypePicker
                  leaveTypes={leaveTypes}
                  value={form.leaveTypeId}
                  onChange={(nextLeaveTypeId) => updateFormField("leaveTypeId", nextLeaveTypeId)}
                  disabled={loading}
                  storageScope={currentUserId || "self"}
                />
                {selectedType && (
                  <div className="lrcm-help">
                    {t(`leaves:modal.accountingTypes.${selectedAccountingType}`, {
                      defaultValue: selectedAccountingType,
                    })}
                  </div>
                )}
              </div>

              {/* ---------------------------------------------------------------- */}
              {/* Approver                                                          */}
              {/* ---------------------------------------------------------------- */}
              <div className="lrcm-field">
                <label>{entitlementContextKind === "SICK_LEAVE" ? t("leaves:modal.hrConfirmationApprover", { defaultValue: "HR confirmation approver" }) : t("leaves:common.approver")}</label>
                <select
                  value={form.approverId}
                  onChange={(e) => updateFormField("approverId", e.target.value)}
                  disabled={loading}
                >
                  <option value={AUTO_APPROVER_VALUE}>
                    {t("leaves:modal.approverAuto")}
                  </option>

                  {approvers.map((approver) => (
                    <option key={approver.id} value={approver.id}>
                      {formatApproverLabel(approver)}
                    </option>
                  ))}
                </select>

                <div className="lrcm-help">
                  {entitlementContextKind === "SICK_LEAVE"
                    ? t("leaves:modal.hrConfirmationApproverHelp", { defaultValue: "For L4, this is HR confirmation of the e-ZLA/PUE record, not legal approval of the illness." })
                    : t("leaves:modal.approverAutoHelp")}
                </div>
              </div>

              <div className="lrcm-divider" />

              {/* ---------------------------------------------------------------- */}
              {/* Cancellation workflow                                             */}
              {/* ---------------------------------------------------------------- */}
              {isCancellation ? (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.requestToCancel")}</label>

                  {cancellableLoading ? (
                    <div className="lrcm-help">{t("leaves:modal.loadCancellable")}</div>
                  ) : cancellableLeaves.length === 0 ? (
                    <div className="lrcm-help">
                      {t("leaves:modal.noCancellable")}
                    </div>
                  ) : (
                    <select
                      value={form.cancellationTargetId}
                      onChange={(e) =>
                        updateFormField("cancellationTargetId", e.target.value)
                      }
                      disabled={loading}
                    >
                      {cancellableLeaves.map((row) => (
                        <option key={row.id} value={row.id}>
                          {formatCancellableLeaveOption(row, t)}
                        </option>
                      ))}
                    </select>
                  )}

                  {cancellationTarget && (
                    <div className="lrcm-help" style={{ marginTop: 6 }}>
                      {t("leaves:modal.targetStatus")} <b>{cancellationTarget.status}</b>.
                      {String(cancellationTarget.status).toUpperCase() === "PENDING" ? (
                        <>{" "}{t("leaves:modal.pendingAutoCancel")}</>
                      ) : (
                        <>{" "}{t("leaves:modal.approvedCancellationWorkflow")}</>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* -------------------------------------------------------------- */}
                  {/* Partial day toggle                                              */}
                  {/* -------------------------------------------------------------- */}
                  <div className="lrcm-field lrcm-field--span2">
                    <label style={{ textTransform: "none", letterSpacing: 0 }}>
                      <input
                        type="checkbox"
                        checked={form.useHoursMode}
                        onChange={(e) => updateFormField("useHoursMode", e.target.checked)}
                        disabled={loading || !supportsPartialDay || isTimeBalanceEvent}
                        style={{ marginRight: 10 }}
                      />
                      {t("leaves:modal.useHoursMode")}
                    </label>

                    <div className="lrcm-help">
                      {t("leaves:modal.hoursModeHelp")}
                    </div>
                  </div>

                  {!effectiveHoursMode ? (
                    <>
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.startDate")}</label>
                        <input
                          type="date"
                          value={form.startDate}
                          onChange={(e) => updateFormField("startDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>

                      <div className="lrcm-field">
                        <label>{t("leaves:modal.endDateInclusive")}</label>
                        <input
                          type="date"
                          value={form.endDateInclusive}
                          onChange={(e) =>
                            updateFormField("endDateInclusive", e.target.value)
                          }
                          disabled={loading}
                        />

                        <div className="lrcm-help">
                          {t("leaves:modal.endExclusiveHelp")}
                        </div>
                      </div>
                    </>
                  ) : isTimeBalanceEvent ? (
                    <div className="lrcm-field lrcm-field--span2">
                      <LeaveTimeIntervalPicker
                        startDT={form.startDT}
                        endDT={form.endDT}
                        eventKind={selectedAccountingType}
                        disabled={loading}
                        onChange={({ startDT, endDT }) => {
                          setForm((prev) => ({
                            ...prev,
                            startDT,
                            endDT,
                          }));
                        }}
                      />

                      <div className="lrcm-help">
                        {t("leaves:modal.timeInterval.backendContractHelp", {
                          defaultValue: "The picker is optimized for short work-time adjustments. The backend still receives normalized start and end timestamps, so settlement and future calendar logic remain consistent.",
                        })}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.startDateTime")}</label>
                        <input
                          type="datetime-local"
                          value={form.startDT}
                          onChange={(e) => updateFormField("startDT", e.target.value)}
                          disabled={loading}
                        />
                      </div>

                      <div className="lrcm-field">
                        <label>{t("leaves:modal.endDateTime")}</label>
                        <input
                          type="datetime-local"
                          value={form.endDT}
                          onChange={(e) => updateFormField("endDT", e.target.value)}
                          disabled={loading}
                        />

                        <div className="lrcm-help">
                          {t("leaves:modal.hoursModeCalculationHelp")}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {!isCancellation && isTimeBalanceEvent && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>
                    {t(`leaves:modal.timeBalance.${timeBalanceTitleKey}`, {
                      defaultValue: isAddTimeDebtEvent
                        ? "Private exit to make up"
                        : "Make-up work",
                    })}
                  </label>
                  <div className="lrcm-help" style={{ marginBottom: 8 }}>
                    {t(`leaves:modal.timeBalance.${timeBalanceHelpKey}`, {
                      defaultValue: isAddTimeDebtEvent
                        ? "This private exit decreases the employee work-time balance after approval. Existing or future make-up work can bring the balance back up."
                        : "This make-up work increases the employee work-time balance after approval. It can compensate existing private exits or remain available as positive time balance.",
                    })}
                  </div>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.timeBalance.sharedCalendarHelp", {
                      defaultValue: "Both work-time balance events remain visible for the absence calendar. The backend stores settlement links so HR can later see which event compensated which other event.",
                    })}
                  </div>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.timeBalance.settlementMode", { defaultValue: "Settlement mode" })}</label>
                      <select value={form.timeSettlementMode} onChange={(e) => updateFormField("timeSettlementMode", e.target.value)} disabled={loading}>
                        <option value={TIME_SETTLEMENT_MODE_AUTO}>{t("leaves:modal.timeBalance.modes.AUTO_FIFO", { defaultValue: "Automatic FIFO" })}</option>
                        <option value={TIME_SETTLEMENT_MODE_MANUAL}>{t("leaves:modal.timeBalance.modes.MANUAL", { defaultValue: "Select specific entry" })}</option>
                        <option value={TIME_SETTLEMENT_MODE_NONE}>{t("leaves:modal.timeBalance.modes.NONE", { defaultValue: "Leave open" })}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.timeBalance.availableOppositeEntries", { defaultValue: "Available opposite entries" })}</label>
                      <div className="lrcm-help">
                        {settlementCandidatesLoading
                          ? t("leaves:modal.timeBalance.loadingCandidates", { defaultValue: "Loading candidates…" })
                          : t("leaves:modal.timeBalance.candidateCount", { defaultValue: "{{count}} candidate(s)", count: settlementCandidates.length })}
                      </div>
                    </div>

                    {form.timeSettlementMode === TIME_SETTLEMENT_MODE_MANUAL && (
                      <div className="lrcm-field" style={{ gridColumn: "1 / -1" }}>
                        <div className="lrcm-timeSettlementBrowser">
                          <div className="lrcm-timeSettlementBrowser__header">
                            <div>
                              <div className="lrcm-timeSettlementBrowser__title">
                                {t("leaves:modal.timeBalance.manualAllocationTitle", { defaultValue: "Manual allocation" })}
                              </div>
                              <div className="lrcm-help">
                                {t("leaves:modal.timeBalance.manualAllocationHelp", { defaultValue: "Select one or more opposite entries and define how many minutes should be compensated from each one." })}
                              </div>
                            </div>
                            <div className="lrcm-timeSettlementBrowser__summary" aria-live="polite">
                              <strong>{formatMinutesCompact(selectedTimeSettlementAllocationMinutes)}</strong>
                              <span>
                                {currentTimeSettlementDurationMinutes == null
                                  ? t("leaves:modal.timeBalance.allocatedSummaryUnknownDuration", { defaultValue: "allocated" })
                                  : t("leaves:modal.timeBalance.allocatedSummary", {
                                      defaultValue: "of {{duration}} allocated · {{remaining}} left",
                                      duration: formatMinutesCompact(currentTimeSettlementDurationMinutes),
                                      remaining: formatMinutesCompact(remainingTimeSettlementAllocationMinutes || 0),
                                    })}
                              </span>
                            </div>
                          </div>

                          {settlementCandidatesLoading ? (
                            <div className="lrcm-timeSettlementBrowser__empty">
                              {t("leaves:modal.timeBalance.loadingCandidates", { defaultValue: "Loading candidates…" })}
                            </div>
                          ) : settlementCandidates.length === 0 ? (
                            <div className="lrcm-timeSettlementBrowser__empty">
                              {t("leaves:modal.timeBalance.noCandidates", { defaultValue: "There are no approved opposite entries with open minutes." })}
                            </div>
                          ) : (
                            <div className="lrcm-timeSettlementBrowser__list">
                              {settlementCandidates.map((candidate) => {
                                const candidateId = getSettlementCandidateId(candidate);
                                const allocation = getManualSettlementAllocation(candidateId);
                                const checked = Boolean(allocation);
                                const remaining = getCandidateRemainingMinutes(candidate);

                                return (
                                  <div className="lrcm-timeSettlementBrowser__row" key={candidateId}>
                                    <label className="lrcm-timeSettlementBrowser__check">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => toggleManualSettlementCandidate(candidate, e.target.checked)}
                                        disabled={loading}
                                      />
                                      <span>{formatTimeSettlementCandidateLabel(candidate, t)}</span>
                                    </label>

                                    <div className="lrcm-timeSettlementBrowser__amount">
                                      <span className="lrcm-timeSettlementBrowser__open">
                                        {t("leaves:modal.timeBalance.openMinutes", {
                                          defaultValue: "Open: {{minutes}}",
                                          minutes: formatMinutesCompact(remaining),
                                        })}
                                      </span>
                                      <input
                                        type="number"
                                        min="1"
                                        max={remaining || undefined}
                                        step="1"
                                        value={allocation?.minutes || ""}
                                        onChange={(e) => setManualSettlementAllocation(candidateId, e.target.value)}
                                        disabled={loading || !checked}
                                        placeholder={String(remaining)}
                                        aria-label={t("leaves:modal.timeBalance.settlementMinutes", { defaultValue: "Minutes to compensate" })}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {currentTimeSettlementDurationMinutes != null && selectedTimeSettlementAllocationMinutes > currentTimeSettlementDurationMinutes && (
                            <div className="lrcm-timeSettlementBrowser__warning">
                              {t("leaves:modal.timeBalance.allocationExceedsRequest", { defaultValue: "Manual settlement cannot exceed the duration of the new request." })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------------------- */}
              {/* Legal / family entitlement context                                */}
              {/* ---------------------------------------------------------------- */}
              {!isCancellation && entitlementContextKind === "FATHER" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.fatherTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.eventType")}</label>
                      <select
                        value={form.fatherEventType}
                        onChange={(e) => updateFormField("fatherEventType", e.target.value)}
                        disabled={loading}
                      >
                        <option value="BIRTH">{t("leaves:modal.entitlementContext.birth")}</option>
                        <option value="ADOPTION">{t("leaves:modal.entitlementContext.adoption")}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional")}</label>
                      <input
                        type="text"
                        value={form.fatherChildName}
                        onChange={(e) => updateFormField("fatherChildName", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    {form.fatherEventType === "ADOPTION" ? (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.adoptionFinalDate")}</label>
                        <input
                          type="date"
                          value={form.fatherAdoptionFinalDate}
                          onChange={(e) => updateFormField("fatherAdoptionFinalDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    ) : (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.childBirthDate")}</label>
                        <input
                          type="date"
                          value={form.fatherChildBirthDate}
                          onChange={(e) => updateFormField("fatherChildBirthDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    )}
                  </div>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.fatherHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "CHILDCARE_188" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.childcareTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childBirthDate")}</label>
                      <input
                        type="date"
                        value={form.childcareChildBirthDate}
                        onChange={(e) => updateFormField("childcareChildBirthDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional")}</label>
                      <input
                        type="text"
                        value={form.childcareChildName}
                        onChange={(e) => updateFormField("childcareChildName", e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.childcareOtherParentDeclarationConfirmed}
                      onChange={(e) => updateFormField("childcareOtherParentDeclarationConfirmed", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.otherParentDeclaration")}
                  </label>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.childcareHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "CARE_LEAVE" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.careTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.recipientRelation")}</label>
                      <select
                        value={form.careRecipientRelation}
                        onChange={(e) => updateFormField("careRecipientRelation", e.target.value)}
                        disabled={loading}
                      >
                        <option value="">—</option>
                        <option value="SON">{t("leaves:modal.entitlementContext.relations.SON")}</option>
                        <option value="DAUGHTER">{t("leaves:modal.entitlementContext.relations.DAUGHTER")}</option>
                        <option value="MOTHER">{t("leaves:modal.entitlementContext.relations.MOTHER")}</option>
                        <option value="FATHER">{t("leaves:modal.entitlementContext.relations.FATHER")}</option>
                        <option value="SPOUSE">{t("leaves:modal.entitlementContext.relations.SPOUSE")}</option>
                        <option value="HOUSEHOLD_MEMBER">{t("leaves:modal.entitlementContext.relations.HOUSEHOLD_MEMBER")}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.recipientNameOptional")}</label>
                      <input
                        type="text"
                        value={form.careRecipientName}
                        onChange={(e) => updateFormField("careRecipientName", e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {form.careRecipientRelation === "HOUSEHOLD_MEMBER" && (
                    <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.careSameHousehold}
                        onChange={(e) => updateFormField("careSameHousehold", e.target.checked)}
                        disabled={loading}
                        style={{ marginRight: 10 }}
                      />
                      {t("leaves:modal.entitlementContext.sameHousehold")}
                    </label>
                  )}

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.careSeriousMedicalReasonDeclared}
                      onChange={(e) => updateFormField("careSeriousMedicalReasonDeclared", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.seriousMedicalReason")}
                  </label>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.careHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "EDUCATION" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.educationTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.educationReason")}</label>
                      <select
                        value={form.educationReason}
                        onChange={(e) => updateFormField("educationReason", e.target.value)}
                        disabled={loading}
                      >
                        <option value="">—</option>
                        <option value="EXTERNAL_EXAM">{t("leaves:modal.entitlementContext.educationReasons.EXTERNAL_EXAM")}</option>
                        <option value="MATURITY_EXAM">{t("leaves:modal.entitlementContext.educationReasons.MATURITY_EXAM")}</option>
                        <option value="PROFESSIONAL_EXAM">{t("leaves:modal.entitlementContext.educationReasons.PROFESSIONAL_EXAM")}</option>
                        <option value="VOCATIONAL_QUALIFICATION_EXAM">{t("leaves:modal.entitlementContext.educationReasons.VOCATIONAL_QUALIFICATION_EXAM")}</option>
                        <option value="DIPLOMA_THESIS_AND_EXAM">{t("leaves:modal.entitlementContext.educationReasons.DIPLOMA_THESIS_AND_EXAM")}</option>
                        <option value="EMPLOYER_APPROVED_OTHER">{t("leaves:modal.entitlementContext.educationReasons.EMPLOYER_APPROVED_OTHER")}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.institutionNameOptional")}</label>
                      <input
                        type="text"
                        value={form.educationInstitutionName}
                        onChange={(e) => updateFormField("educationInstitutionName", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.studyProgramOptional")}</label>
                      <input
                        type="text"
                        value={form.educationStudyProgramName}
                        onChange={(e) => updateFormField("educationStudyProgramName", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    {form.educationReason === "DIPLOMA_THESIS_AND_EXAM" ? (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.diplomaExamDate")}</label>
                        <input
                          type="date"
                          value={form.educationDiplomaExamDate}
                          onChange={(e) => updateFormField("educationDiplomaExamDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    ) : (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.examDate")}</label>
                        <input
                          type="date"
                          value={form.educationExamDate}
                          onChange={(e) => updateFormField("educationExamDate", e.target.value)}
                          disabled={loading || form.educationReason === "EMPLOYER_APPROVED_OTHER"}
                        />
                      </div>
                    )}

                    {form.educationReason === "EMPLOYER_APPROVED_OTHER" && (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.customEntitlementDays")}</label>
                        <input
                          type="number"
                          min="0.5"
                          max="21"
                          step="0.5"
                          value={form.educationCustomEntitlementDays}
                          onChange={(e) => updateFormField("educationCustomEntitlementDays", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    )}
                  </div>

                  {form.educationReason === "DIPLOMA_THESIS_AND_EXAM" && (
                    <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.educationLastYearOfStudies}
                        onChange={(e) => updateFormField("educationLastYearOfStudies", e.target.checked)}
                        disabled={loading}
                        style={{ marginRight: 10 }}
                      />
                      {t("leaves:modal.entitlementContext.lastYearOfStudies")}
                    </label>
                  )}

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.educationEmployerConsentConfirmed}
                      onChange={(e) => updateFormField("educationEmployerConsentConfirmed", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.employerConsentConfirmed")}
                  </label>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.educationHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "HOLIDAY_COMPENSATION" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.holidayCompensationTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.holidayDate")}</label>
                      <input
                        type="date"
                        value={form.holidayDate}
                        onChange={(e) => updateFormField("holidayDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.holidayNameOptional")}</label>
                      <input
                        type="text"
                        value={form.holidayName}
                        onChange={(e) => updateFormField("holidayName", e.target.value)}
                        disabled={loading}
                        placeholder={t("leaves:modal.entitlementContext.holidayNamePlaceholder")}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.settlementPeriodStart")}</label>
                      <input
                        type="date"
                        value={form.holidaySettlementPeriodStart}
                        onChange={(e) => updateFormField("holidaySettlementPeriodStart", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.settlementPeriodEnd")}</label>
                      <input
                        type="date"
                        value={form.holidaySettlementPeriodEnd}
                        onChange={(e) => updateFormField("holidaySettlementPeriodEnd", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.compensationMinutes")}</label>
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        step="1"
                        value={form.holidayCompensationMinutes}
                        onChange={(e) => updateFormField("holidayCompensationMinutes", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.mustBeTakenUntilOptional")}</label>
                      <input
                        type="date"
                        value={form.holidayMustBeTakenUntil}
                        onChange={(e) => updateFormField("holidayMustBeTakenUntil", e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.holidayCompensationHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "BLOOD_DONATION" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.bloodDonationTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.donationDate")}</label>
                      <input
                        type="date"
                        value={form.bloodDonationDate}
                        onChange={(e) => updateFormField("bloodDonationDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.donationCenterOptional")}</label>
                      <input
                        type="text"
                        value={form.bloodDonationCenterName}
                        onChange={(e) => updateFormField("bloodDonationCenterName", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateNumberOptional")}</label>
                      <input
                        type="text"
                        value={form.bloodCertificateNumber}
                        onChange={(e) => updateFormField("bloodCertificateNumber", e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.bloodMedicalExamOnly}
                      onChange={(e) => updateFormField("bloodMedicalExamOnly", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.medicalExamOnly")}
                  </label>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.bloodCertificateReceived}
                      onChange={(e) => updateFormField("bloodCertificateReceived", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.certificateReceived")}
                  </label>

                  {!form.bloodMedicalExamOnly && (
                    <>
                      <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={form.bloodDonationSuccessful}
                          onChange={(e) => updateFormField("bloodDonationSuccessful", e.target.checked)}
                          disabled={loading}
                          style={{ marginRight: 10 }}
                        />
                        {t("leaves:modal.entitlementContext.donationSuccessful")}
                      </label>

                      <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={form.bloodIncludesNextDayOff}
                          onChange={(e) => updateFormField("bloodIncludesNextDayOff", e.target.checked)}
                          disabled={loading}
                          style={{ marginRight: 10 }}
                        />
                        {t("leaves:modal.entitlementContext.includesNextDayOff")}
                      </label>
                    </>
                  )}

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.bloodDonationHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "MATERNITY" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.maternityTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.expectedBirthDate")}</label>
                      <input
                        type="date"
                        value={form.maternityExpectedBirthDate}
                        onChange={(e) => updateFormField("maternityExpectedBirthDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childBirthDateOptional")}</label>
                      <input
                        type="date"
                        value={form.maternityChildBirthDate}
                        onChange={(e) => updateFormField("maternityChildBirthDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.numberOfChildrenAtBirth")}</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        value={form.maternityNumberOfChildrenAtBirth}
                        onChange={(e) => updateFormField("maternityNumberOfChildrenAtBirth", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.preBirthLeaveStartDateOptional")}</label>
                      <input
                        type="date"
                        value={form.maternityPreBirthLeaveStartDate}
                        onChange={(e) => updateFormField("maternityPreBirthLeaveStartDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.maternityLeaveVariant")}</label>
                      <select
                        value={form.maternityLeaveVariant}
                        onChange={(e) => updateFormField("maternityLeaveVariant", e.target.value)}
                        disabled={loading}
                      >
                        <option value="STANDARD">{t("leaves:modal.entitlementContext.maternityVariants.STANDARD")}</option>
                        <option value="PRE_BIRTH">{t("leaves:modal.entitlementContext.maternityVariants.PRE_BIRTH")}</option>
                        <option value="POST_BIRTH">{t("leaves:modal.entitlementContext.maternityVariants.POST_BIRTH")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.maternityHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "PARENTAL" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.parentalTitle")}</label>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.eventType")}</label>
                      <select
                        value={form.parentalEventType}
                        onChange={(e) => updateFormField("parentalEventType", e.target.value)}
                        disabled={loading}
                      >
                        <option value="BIRTH">{t("leaves:modal.entitlementContext.birth")}</option>
                        <option value="ADOPTION">{t("leaves:modal.entitlementContext.adoption")}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional")}</label>
                      <input
                        type="text"
                        value={form.parentalChildName}
                        onChange={(e) => updateFormField("parentalChildName", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    {form.parentalEventType === "ADOPTION" ? (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.adoptionFinalDate")}</label>
                        <input
                          type="date"
                          value={form.parentalAdoptionFinalDate}
                          onChange={(e) => updateFormField("parentalAdoptionFinalDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    ) : (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.childBirthDate")}</label>
                        <input
                          type="date"
                          value={form.parentalChildBirthDate}
                          onChange={(e) => updateFormField("parentalChildBirthDate", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    )}

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.numberOfChildrenAtBirth")}</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        value={form.parentalNumberOfChildrenAtBirth}
                        onChange={(e) => updateFormField("parentalNumberOfChildrenAtBirth", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.parentalLeaveVariant")}</label>
                      <select
                        value={form.parentalLeaveVariant}
                        onChange={(e) => updateFormField("parentalLeaveVariant", e.target.value)}
                        disabled={loading}
                      >
                        <option value="STANDARD_70">{t("leaves:modal.entitlementContext.parentalVariants.STANDARD_70")}</option>
                        <option value="AVERAGED_81_5">{t("leaves:modal.entitlementContext.parentalVariants.AVERAGED_81_5")}</option>
                        <option value="NON_TRANSFERABLE_9_WEEKS_70">{t("leaves:modal.entitlementContext.parentalVariants.NON_TRANSFERABLE_9_WEEKS_70")}</option>
                        <option value="CUSTOM">{t("leaves:modal.entitlementContext.parentalVariants.CUSTOM")}</option>
                      </select>
                    </div>

                    {form.parentalLeaveVariant === "CUSTOM" && (
                      <div className="lrcm-field">
                        <label>{t("leaves:modal.entitlementContext.customPayrollRatePercent")}</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={form.parentalCustomPaymentRatePercent}
                          onChange={(e) => updateFormField("parentalCustomPaymentRatePercent", e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    )}

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.otherParentDeclaredUsageWeeks")}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.parentalOtherParentDeclaredUsageWeeks}
                        onChange={(e) => updateFormField("parentalOtherParentDeclaredUsageWeeks", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    {form.parentalCombinedWithWork && (
                      <>
                        <div className="lrcm-field">
                          <label>{t("leaves:modal.entitlementContext.workFractionDuringLeave")}</label>
                          <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={form.parentalWorkFractionDuringLeave}
                            onChange={(e) => updateFormField("parentalWorkFractionDuringLeave", e.target.value)}
                            disabled={loading}
                          />
                        </div>

                        <div className="lrcm-field">
                          <label>{t("leaves:modal.entitlementContext.extendedEntitlementWeeks")}</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.parentalExtendedEntitlementWeeks}
                            onChange={(e) => updateFormField("parentalExtendedEntitlementWeeks", e.target.value)}
                            disabled={loading}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.parentalHasZaZyciemCertificate}
                      onChange={(e) => updateFormField("parentalHasZaZyciemCertificate", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.hasZaZyciemCertificate")}
                  </label>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.parentalOwnNonTransferablePartUsed}
                      onChange={(e) => updateFormField("parentalOwnNonTransferablePartUsed", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.ownNonTransferablePartUsed")}
                  </label>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.parentalCombinedWithWork}
                      onChange={(e) => updateFormField("parentalCombinedWithWork", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.combinedWithWork")}
                  </label>

                  <div className="lrcm-help">
                    {t("leaves:modal.entitlementContext.parentalHelp")}
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "SICK_LEAVE" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.sickLeaveTitle", { defaultValue: "Sick leave / L4 certificate" })}</label>

                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.sickLeaveHelp", { defaultValue: "The selected date range is treated as the incapacity period. HR confirmation is process confirmation against e-ZLA/PUE, not legal approval of illness." })}
                  </div>

                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.sickReasonType", { defaultValue: "Sick leave reason" })}</label>
                      <select
                        value={form.sickReasonType}
                        onChange={(e) => {
                          const value = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            sickReasonType: value,
                            sickPayrollPaymentRatePercent: getDefaultSickPayrollRatePercent(value),
                          }));
                        }}
                        disabled={loading}
                      >
                        <option value="STANDARD">{t("leaves:modal.entitlementContext.sickReasons.STANDARD", { defaultValue: "Standard sick leave" })}</option>
                        <option value="PREGNANCY">{t("leaves:modal.entitlementContext.sickReasons.PREGNANCY", { defaultValue: "Pregnancy sick leave" })}</option>
                        <option value="ACCIDENT_AT_WORK">{t("leaves:modal.entitlementContext.sickReasons.ACCIDENT_AT_WORK", { defaultValue: "Accident at work" })}</option>
                        <option value="OCCUPATIONAL_DISEASE">{t("leaves:modal.entitlementContext.sickReasons.OCCUPATIONAL_DISEASE", { defaultValue: "Occupational disease" })}</option>
                        <option value="HOSPITAL">{t("leaves:modal.entitlementContext.sickReasons.HOSPITAL", { defaultValue: "Hospital" })}</option>
                        <option value="CHILD_CARE_SICKNESS">{t("leaves:modal.entitlementContext.sickReasons.CHILD_CARE_SICKNESS", { defaultValue: "Child-care sickness" })}</option>
                        <option value="OTHER">{t("leaves:modal.entitlementContext.sickReasons.OTHER", { defaultValue: "Other" })}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateSource", { defaultValue: "Certificate source" })}</label>
                      <select
                        value={form.sickCertificateSource}
                        onChange={(e) => updateFormField("sickCertificateSource", e.target.value)}
                        disabled={loading}
                      >
                        <option value="MANUAL">{t("leaves:modal.entitlementContext.sickCertificateSources.MANUAL", { defaultValue: "Manual entry" })}</option>
                        <option value="PUE_ZUS">{t("leaves:modal.entitlementContext.sickCertificateSources.PUE_ZUS", { defaultValue: "PUE/ZUS e-ZLA" })}</option>
                        <option value="PAPER_BACKUP">{t("leaves:modal.entitlementContext.sickCertificateSources.PAPER_BACKUP", { defaultValue: "Paper backup" })}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateNumberOptional", { defaultValue: "Certificate number (optional)" })}</label>
                      <input
                        type="text"
                        value={form.sickCertificateNumber}
                        onChange={(e) => updateFormField("sickCertificateNumber", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.sickPayrollPaymentRatePercent", { defaultValue: "Payroll payment rate %" })}</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={form.sickPayrollPaymentRatePercent}
                        onChange={(e) => updateFormField("sickPayrollPaymentRatePercent", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.issueDateOptional", { defaultValue: "Issue date (optional)" })}</label>
                      <input
                        type="date"
                        value={form.sickIssueDate}
                        onChange={(e) => updateFormField("sickIssueDate", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.receivedAtOptional", { defaultValue: "Received at (optional)" })}</label>
                      <input
                        type="date"
                        value={form.sickReceivedAt}
                        onChange={(e) => updateFormField("sickReceivedAt", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.doctorOrMedicalUnitOptional", { defaultValue: "Doctor / medical unit (optional)" })}</label>
                      <input
                        type="text"
                        value={form.sickDoctorOrMedicalUnit}
                        onChange={(e) => updateFormField("sickDoctorOrMedicalUnit", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.benefitPayer", { defaultValue: "Benefit payer" })}</label>
                      <select
                        value={form.sickBenefitPayer}
                        onChange={(e) => updateFormField("sickBenefitPayer", e.target.value)}
                        disabled={loading}
                      >
                        <option value="UNKNOWN">{t("leaves:modal.entitlementContext.sickBenefitPayers.UNKNOWN", { defaultValue: "Unknown" })}</option>
                        <option value="EMPLOYER">{t("leaves:modal.entitlementContext.sickBenefitPayers.EMPLOYER", { defaultValue: "Employer" })}</option>
                        <option value="ZUS">{t("leaves:modal.entitlementContext.sickBenefitPayers.ZUS", { defaultValue: "ZUS" })}</option>
                      </select>
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.employerSickPayDaysUsedBeforeOptional", { defaultValue: "Employer sick-pay days used before (optional)" })}</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.sickEmployerSickPayDaysUsedBefore}
                        onChange={(e) => updateFormField("sickEmployerSickPayDaysUsedBefore", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.sickPayDaySequenceStartOptional", { defaultValue: "Sick-pay day sequence start (optional)" })}</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.sickPayDaySequenceStart}
                        onChange={(e) => updateFormField("sickPayDaySequenceStart", e.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.sickPayDaySequenceEndOptional", { defaultValue: "Sick-pay day sequence end (optional)" })}</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.sickPayDaySequenceEnd}
                        onChange={(e) => updateFormField("sickPayDaySequenceEnd", e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.sickEZlaConfirmed}
                      onChange={(e) => updateFormField("sickEZlaConfirmed", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.entitlementContext.eZlaConfirmed", { defaultValue: "e-ZLA/PUE confirmation checked" })}
                  </label>
                </div>
              )}


              {!isCancellation && entitlementContextKind === "PREGNANCY_MEDICAL_EXAM" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.pregnancyExamTitle", { defaultValue: "Pregnancy medical examination" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.pregnancyExamHelp", { defaultValue: "This is a paid release from work for pregnancy-related medical examinations, not SICK_LEAVE/L4. HR confirms the document and the payroll rate is 100%." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.examDate", { defaultValue: "Exam date" })}</label>
                      <input type="date" value={form.pregnancyExamDate} onChange={(e) => updateFormField("pregnancyExamDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateNumberOptional", { defaultValue: "Certificate number (optional)" })}</label>
                      <input type="text" value={form.pregnancyExamCertificateNumber} onChange={(e) => updateFormField("pregnancyExamCertificateNumber", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field lrcm-field--span2">
                      <label>{t("leaves:modal.entitlementContext.doctorOrMedicalUnitOptional", { defaultValue: "Doctor / medical unit (optional)" })}</label>
                      <input type="text" value={form.pregnancyExamDoctorOrMedicalUnit} onChange={(e) => updateFormField("pregnancyExamDoctorOrMedicalUnit", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.pregnancyExamMedicalCertificateConfirmed} onChange={(e) => updateFormField("pregnancyExamMedicalCertificateConfirmed", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.medicalCertificateConfirmed", { defaultValue: "Medical / pregnancy documentation confirmed by HR" })}
                  </label>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.pregnancyExamCannotBeDoneOutsideWorkingHours} onChange={(e) => updateFormField("pregnancyExamCannotBeDoneOutsideWorkingHours", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.cannotBeDoneOutsideWorkingHours", { defaultValue: "Exam cannot be done outside working hours" })}
                  </label>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "ADOPTION_MATERNITY" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.adoptionMaternityTitle", { defaultValue: "Adoption maternity-equivalent leave" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.adoptionMaternityHelp", { defaultValue: "Used for leave on maternity-leave terms after accepting a child for adoption/foster care. It is separate from parental leave." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.adoptionFinalDate", { defaultValue: "Adoption / acceptance date" })}</label>
                      <input type="date" value={form.adoptionMaternityFinalDate} onChange={(e) => updateFormField("adoptionMaternityFinalDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.numberOfChildrenAtBirth", { defaultValue: "Number of children" })}</label>
                      <input type="number" min="1" step="1" value={form.adoptionMaternityNumberOfChildren} onChange={(e) => updateFormField("adoptionMaternityNumberOfChildren", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional", { defaultValue: "Child name (optional)" })}</label>
                      <input type="text" value={form.adoptionMaternityChildName} onChange={(e) => updateFormField("adoptionMaternityChildName", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childBirthDateOptional", { defaultValue: "Child birth date (optional)" })}</label>
                      <input type="date" value={form.adoptionMaternityChildBirthDate} onChange={(e) => updateFormField("adoptionMaternityChildBirthDate", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "SUPPLEMENTARY_MATERNITY" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.supplementaryMaternityTitle", { defaultValue: "Supplementary maternity leave" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.supplementaryMaternityHelp", { defaultValue: "Additional maternity leave for premature or hospitalised newborn cases. The entitlement weeks are stored as an HR-confirmed snapshot." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childBirthDate", { defaultValue: "Child birth date" })}</label>
                      <input type="date" value={form.supplementaryMaternityChildBirthDate} onChange={(e) => updateFormField("supplementaryMaternityChildBirthDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional", { defaultValue: "Child name (optional)" })}</label>
                      <input type="text" value={form.supplementaryMaternityChildName} onChange={(e) => updateFormField("supplementaryMaternityChildName", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.pregnancyWeekAtBirth", { defaultValue: "Pregnancy week at birth" })}</label>
                      <input type="number" min="0" step="1" value={form.supplementaryMaternityPregnancyWeekAtBirth} onChange={(e) => updateFormField("supplementaryMaternityPregnancyWeekAtBirth", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.birthWeightGrams", { defaultValue: "Birth weight (grams)" })}</label>
                      <input type="number" min="0" step="1" value={form.supplementaryMaternityBirthWeightGrams} onChange={(e) => updateFormField("supplementaryMaternityBirthWeightGrams", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.hospitalizationStartDate", { defaultValue: "Hospitalization start" })}</label>
                      <input type="date" value={form.supplementaryMaternityHospitalizationStartDate} onChange={(e) => updateFormField("supplementaryMaternityHospitalizationStartDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.hospitalizationEndDate", { defaultValue: "Hospitalization end" })}</label>
                      <input type="date" value={form.supplementaryMaternityHospitalizationEndDate} onChange={(e) => updateFormField("supplementaryMaternityHospitalizationEndDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.entitlementWeeks", { defaultValue: "Entitlement weeks" })}</label>
                      <input type="number" min="1" max="15" step="1" value={form.supplementaryMaternityEntitlementWeeks} onChange={(e) => updateFormField("supplementaryMaternityEntitlementWeeks", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.supplementaryMaternityMedicalCertificateConfirmed} onChange={(e) => updateFormField("supplementaryMaternityMedicalCertificateConfirmed", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.medicalCertificateConfirmed", { defaultValue: "Medical certificate confirmed by HR" })}
                  </label>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "CARE_ALLOWANCE" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.careAllowanceTitle", { defaultValue: "Care allowance absence" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.careAllowanceHelp", { defaultValue: "Use for care allowance situations such as sick child, sick family member or care closure. This is separate from employee SICK_LEAVE/L4." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.careAllowanceReasonType", { defaultValue: "Care reason" })}</label>
                      <select value={form.careAllowanceReasonType} onChange={(e) => updateFormField("careAllowanceReasonType", e.target.value)} disabled={loading}>
                        <option value="SICK_CHILD_UNDER_14">{t("leaves:modal.entitlementContext.careAllowanceReasons.SICK_CHILD_UNDER_14", { defaultValue: "Sick child under 14" })}</option>
                        <option value="SICK_CHILD_OVER_14">{t("leaves:modal.entitlementContext.careAllowanceReasons.SICK_CHILD_OVER_14", { defaultValue: "Sick child over 14" })}</option>
                        <option value="SICK_FAMILY_MEMBER">{t("leaves:modal.entitlementContext.careAllowanceReasons.SICK_FAMILY_MEMBER", { defaultValue: "Sick family member" })}</option>
                        <option value="HEALTHY_CHILD_CARE_CLOSURE">{t("leaves:modal.entitlementContext.careAllowanceReasons.HEALTHY_CHILD_CARE_CLOSURE", { defaultValue: "Healthy child care / closure" })}</option>
                        <option value="DISABLED_CHILD_CARE">{t("leaves:modal.entitlementContext.careAllowanceReasons.DISABLED_CHILD_CARE", { defaultValue: "Disabled child care" })}</option>
                        <option value="OTHER">{t("leaves:modal.entitlementContext.careAllowanceReasons.OTHER", { defaultValue: "Other" })}</option>
                      </select>
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.recipientRelation", { defaultValue: "Recipient relation" })}</label>
                      <input type="text" value={form.careAllowanceRecipientRelation} onChange={(e) => updateFormField("careAllowanceRecipientRelation", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.recipientNameOptional", { defaultValue: "Recipient name (optional)" })}</label>
                      <input type="text" value={form.careAllowanceRecipientName} onChange={(e) => updateFormField("careAllowanceRecipientName", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.recipientBirthDateOptional", { defaultValue: "Recipient birth date (optional)" })}</label>
                      <input type="date" value={form.careAllowanceRecipientBirthDate} onChange={(e) => updateFormField("careAllowanceRecipientBirthDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateSource", { defaultValue: "Certificate source" })}</label>
                      <select value={form.careAllowanceCertificateSource} onChange={(e) => updateFormField("careAllowanceCertificateSource", e.target.value)} disabled={loading}>
                        <option value="MANUAL">{t("leaves:modal.entitlementContext.sickCertificateSources.MANUAL", { defaultValue: "Manual entry" })}</option>
                        <option value="PUE_ZUS">{t("leaves:modal.entitlementContext.sickCertificateSources.PUE_ZUS", { defaultValue: "PUE/ZUS e-ZLA" })}</option>
                        <option value="PAPER_BACKUP">{t("leaves:modal.entitlementContext.sickCertificateSources.PAPER_BACKUP", { defaultValue: "Paper backup" })}</option>
                      </select>
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.certificateNumberOptional", { defaultValue: "Certificate number (optional)" })}</label>
                      <input type="text" value={form.careAllowanceCertificateNumber} onChange={(e) => updateFormField("careAllowanceCertificateNumber", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.careAllowanceLimitDaysSnapshot", { defaultValue: "Limit days snapshot" })}</label>
                      <input type="number" min="0" step="1" value={form.careAllowanceLimitDaysSnapshot} onChange={(e) => updateFormField("careAllowanceLimitDaysSnapshot", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.sickPayrollPaymentRatePercent", { defaultValue: "Payroll payment rate %" })}</label>
                      <input type="number" min="0" max="100" step="0.01" value={form.careAllowancePayrollPaymentRatePercent} onChange={(e) => updateFormField("careAllowancePayrollPaymentRatePercent", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.careAllowanceCertificateReceived} onChange={(e) => updateFormField("careAllowanceCertificateReceived", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.certificateReceived", { defaultValue: "Certificate/document received or confirmed" })}
                  </label>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "CHILDCARE_LONG" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.childcareLongTitle", { defaultValue: "Long childcare leave" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.childcareLongHelp", { defaultValue: "Long unpaid childcare leave. This is separate from parental leave and from the 2 days / 16 hours childcare right." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childBirthDateOptional", { defaultValue: "Child birth date" })}</label>
                      <input type="date" value={form.childcareLongChildBirthDate} onChange={(e) => updateFormField("childcareLongChildBirthDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.adoptionFinalDate", { defaultValue: "Adoption / acceptance date" })}</label>
                      <input type="date" value={form.childcareLongAdoptionFinalDate} onChange={(e) => updateFormField("childcareLongAdoptionFinalDate", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childNameOptional", { defaultValue: "Child name (optional)" })}</label>
                      <input type="text" value={form.childcareLongChildName} onChange={(e) => updateFormField("childcareLongChildName", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.childcareLongEntitlementMonths", { defaultValue: "Entitlement months" })}</label>
                      <input type="number" min="1" max="36" step="1" value={form.childcareLongEntitlementMonths} onChange={(e) => updateFormField("childcareLongEntitlementMonths", e.target.value)} disabled={loading} />
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.otherParentDeclaredUsageMonths", { defaultValue: "Other parent declared usage months" })}</label>
                      <input type="number" min="0" max="36" step="0.01" value={form.childcareLongOtherParentDeclaredUsageMonths} onChange={(e) => updateFormField("childcareLongOtherParentDeclaredUsageMonths", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.childcareLongDisabledChildCertificate} onChange={(e) => updateFormField("childcareLongDisabledChildCertificate", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.disabledChildCertificate", { defaultValue: "Disabled child certificate / extended validity context" })}
                  </label>
                </div>
              )}

              {!isCancellation && entitlementContextKind === "JOB_SEARCH" && (
                <div className="lrcm-field lrcm-field--span2">
                  <label>{t("leaves:modal.entitlementContext.jobSearchTitle", { defaultValue: "Job-search leave" })}</label>
                  <div className="lrcm-help" style={{ marginBottom: 10 }}>
                    {t("leaves:modal.entitlementContext.jobSearchHelp", { defaultValue: "Paid leave for job search during employer notice period. Entitlement is 2 or 3 days depending on the notice period." })}
                  </div>
                  <div className="lrcm-grid" style={{ padding: 0, gridTemplateColumns: "1fr 1fr" }}>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.noticePeriodType", { defaultValue: "Notice period" })}</label>
                      <select value={form.jobSearchNoticePeriodType} onChange={(e) => updateFormField("jobSearchNoticePeriodType", e.target.value)} disabled={loading}>
                        <option value="TWO_WEEKS">{t("leaves:modal.entitlementContext.noticePeriods.TWO_WEEKS", { defaultValue: "2 weeks" })}</option>
                        <option value="ONE_MONTH">{t("leaves:modal.entitlementContext.noticePeriods.ONE_MONTH", { defaultValue: "1 month" })}</option>
                        <option value="THREE_MONTHS">{t("leaves:modal.entitlementContext.noticePeriods.THREE_MONTHS", { defaultValue: "3 months" })}</option>
                      </select>
                    </div>
                    <div className="lrcm-field">
                      <label>{t("leaves:modal.entitlementContext.employmentTerminationDate", { defaultValue: "Employment termination date" })}</label>
                      <input type="date" value={form.jobSearchEmploymentTerminationDate} onChange={(e) => updateFormField("jobSearchEmploymentTerminationDate", e.target.value)} disabled={loading} />
                    </div>
                  </div>
                  <label style={{ textTransform: "none", letterSpacing: 0, marginTop: 8 }}>
                    <input type="checkbox" checked={form.jobSearchEmployerNoticeConfirmed} onChange={(e) => updateFormField("jobSearchEmployerNoticeConfirmed", e.target.checked)} disabled={loading} style={{ marginRight: 10 }} />
                    {t("leaves:modal.entitlementContext.employerNoticeConfirmed", { defaultValue: "Employer notice period confirmed" })}
                  </label>
                </div>
              )}


              {/* ---------------------------------------------------------------- */}
              {/* Note                                                              */}
              {/* ---------------------------------------------------------------- */}
              <div className="lrcm-field lrcm-field--span2">
                <label>{t("leaves:modal.noteOptional")}</label>
                <textarea
                  value={form.note}
                  onChange={(e) => updateFormField("note", e.target.value)}
                  placeholder={
                    isCancellation
                      ? t("leaves:modal.cancellationReasonPlaceholder")
                      : isTimeBalanceEvent
                        ? t("leaves:modal.timeBalance.notePlaceholder", {
                            defaultValue: "Optional context for the approver, e.g. private appointment reason, agreed make-up work window, or HR comment.",
                          })
                        : t("leaves:modal.approverContextPlaceholder")
                  }
                  disabled={loading}
                  rows={3}
                />
              </div>

              {/* ---------------------------------------------------------------- */}
              {/* Draft (normal requests only)                                      */}
              {/* ---------------------------------------------------------------- */}
              {!isCancellation && (
                <div className="lrcm-field lrcm-field--span2">
                  <label style={{ textTransform: "none", letterSpacing: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.draft}
                      onChange={(e) => updateFormField("draft", e.target.checked)}
                      disabled={loading}
                      style={{ marginRight: 10 }}
                    />
                    {t("leaves:modal.saveAsDraft")}
                  </label>
                </div>
              )}
            </div>

            <div className="lrcm-footer">
              <button
                className="lrcm-btn lrcm-btn--ghost"
                type="button"
                onClick={closeAndReset}
                disabled={loading}
              >
                {t("common:actions.cancel")}
              </button>

              <div className="lrcm-footerRight">
                <button
                  className="lrcm-btn lrcm-btn--primary"
                  type="button"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                >
                  {loading
                    ? t("leaves:modal.creating")
                    : isCancellation
                    ? t("leaves:modal.createCancellation")
                    : t("leaves:modal.create")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}



